import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, Legend } from "recharts";
import { getCurriculum, getTopicCount, getWordCount } from "./curriculum";
import { auth, onAuthChange, signOutUser, loadUserData, saveUserData, deleteAccount, computeFreeze, getTranslationCache, logReviewEvents, getMyReferredStudents, getMyPayoutStatus, requestPayout, uploadTeacherPhoto, TEACHER_PHOTO_MAX_BYTES, TEACHER_PHOTO_TYPES, loadTeacherTermsAcceptance } from "./firebase";
import { I18nProvider, useT } from "./i18n";
import { ThemeContext, useTheme } from "./theme";
import { GLOBAL_CSS } from "./styles";
import { save, load, fmtWpd, daysSince, todayISO, addDays, defaultProgress, cardDist, isolateInterpolation, formatNumber, fmtMoneyCents, getTranslatedTopicName, computeStreakUpdate, getProtectionDays, applyLabSessionCompletion } from "./utils";
import { getPriorCompleteWeek, accountAgeDays, computeWeeklyRecapStats } from "./babiCheckins";
import { TURKISH_MIN, MAX_WORDS, BASE_LANGS, EXTRA_LANGS, TARGET_LANGUAGES, TEACHABLE_LANGUAGES, SELECTABLE_TEACHABLE_LANGUAGES, DAILY_LESSON_LIMIT, getBadge, isPremiumActive, trialDaysLeft, resolveTrialStartDate, PRICE_MONTHLY_USD, PRICE_ANNUAL_USD, ELITE_PRICE_MONTHLY_USD, ELITE_PRICE_YEARLY_USD, VOICE_OVERAGE_MAX_HOURS_PER_PURCHASE, getVoiceTier, VOICE_TIER_ELITE, hasVoiceLabAccess, isTeacher, buildReferralLink } from "./constants";
import { getReferralCodeForCheckout } from "./referralTracking";
import { ConfirmDialog } from "./ConfirmDialog";
import { COUNTRIES } from "./countries";
import { makeD2State, migrateSM2, cardMemState } from "./fsrs";
import { SignInScreen, VerifyEmailScreen } from "./SignInScreen";
import { AuthActionScreen } from "./AuthActionScreen";
import { LessonView, LESSON_POSITION_KEY } from "./LessonView";
import { FlashcardView, ProgressRing } from "./FlashcardView";
import { SettingsView } from "./SettingsView";
import { AgentChat } from "./AgentChat";
import { ListenSpeakLab, LabEliteUpsell } from "./ListenSpeakLab";
import { GrammarLabView } from "./GrammarLabView";
import { GlobalSearch } from "./GlobalSearch";
import { countGrammarNotReviewed, getGrammarPoints, getGrammarPoint } from "./grammarLab";
import { PlacementTestView } from "./PlacementTestView";
import TeacherTermsView from "./TeacherTermsView";
import { TEACHER_TERMS_VERSION, TEACHER_TERMS_DRAFT } from "./teacherTermsContent";
import { pauseSubscription, resumeSubscription, changeVoiceTier, phraseWeeklyRecap } from "./api";

// ═══════════════════════════════════════════════════════════════
// ONBOARDING MESSAGES
// ═══════════════════════════════════════════════════════════════
// These two greet the learner before their base language is known (step 1 IS the
// language picker), so they stay English/Turkish-flavored regardless of locale. From
// the country step onward, OnboardingBody pulls its text from the "onboarding.*" i18n
// keys instead, driven by the `lang` just picked.
const STEPS = [
  { key: "name", q: "Merhaba! 👋 I'm Babi, your personal Turkish companion!\n\nI'm so excited to help you on this journey. First things first — what's your name?" },
  { key: "lang", q: (name) => `Wonderful to meet you, ${name}! 🌟\n\nWhat language would you like to use as your base for learning Turkish? Pick one below, or choose from the full list.` },
];

// ═══════════════════════════════════════════════════════════════
// APP COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function App() {
  // Firebase Auth email links (verify-email / reset-password) land on this exact
  // path with ?mode=&oobCode= — read once at mount, never re-derived, so this
  // stays true for the whole session even after AuthActionScreen's internal
  // navigation changes state. Checked ahead of the normal screen state machine
  // below (before the "loading" return) so an unauthenticated visitor arriving
  // from an email link never gets bounced through onboarding/sign-in gating —
  // see the isAuthActionRoute check right before "if (screen === 'loading')".
  const [isAuthActionRoute] = useState(() => window.location.pathname === "/auth/action");
  // /teacher-dashboard is the one internal screen with a real Hosting path (firebase.json's SPA
  // catch-all serves index.html for it), so a teacher can bookmark or share the link. Captured
  // once at mount — after auth resolves, an existing user is routed straight to that screen
  // instead of the dashboard; TeacherDashboard's own role guard turns away anyone who isn't a
  // teacher. The address bar is then kept in sync by a small effect below (the app has no
  // router otherwise).
  const [teacherDashDeepLink] = useState(() => window.location.pathname === "/teacher-dashboard");
  // /teacher-terms — same real-Hosting-path deep-link mechanism as /teacher-dashboard. The
  // public speafy.com/affiliate/teacher-terms page's "log in to accept" button lands here.
  const [teacherTermsDeepLink] = useState(() => window.location.pathname === "/teacher-terms");
  // The teacher's acceptance record for the CURRENT Terms version, or null if not yet accepted
  // (or not a teacher). Loaded from Firestore after auth resolves — never inferred from a
  // client-writable field. Drives the Home banner + the dashboard "Accepted {date}" line.
  const [teacherTermsAccepted, setTeacherTermsAccepted] = useState(null);
  const [screen, setScreen] = useState("loading");
  const [profile, setProfile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [cards, setCards] = useState([]);
  // Explicit completion ledger — { "T0001": { completedAt }, ... }, keyed by
  // stable topic_id. Written once at lesson completion, never derived from
  // cards (a recycled word's card must not imply topic completion).
  const [completedTopics, setCompletedTopics] = useState({});
  const [grammarProgress, setGrammarProgress] = useState(() => load("grammarProgress") || {});
  const [user, setUser] = useState(null);
  // True when the signed-in account holds an admin custom claim (support_viewer
  // or full_admin — same claim/roles the separate admin app checks). Read
  // directly off the ID token client-side, once per sign-in, in onAuthChange
  // below — not a server round-trip on every isPremiumActive() call. This is
  // a display/gating override only: it never touches `subscription` itself.
  const [isAdminAccount, setIsAdminAccount] = useState(false);
  // simple_viewer: free app access for friends doing informal QA/bug-hunting, same free-access
  // bypass as isAdminAccount above, but zero admin capability (enforced server-side — see
  // functions/adminActions.js VALID_ADMIN_ROLES vs VALID_GRANTABLE_ROLES).
  const [isSimpleViewer, setIsSimpleViewer] = useState(false);
  const uidRef = useRef(null);
  const [selectedTopic, setSelectedTopic] = useState(() => {
    // profile React state isn't loaded yet at this point (mount-time lazy initializer, runs
    // before the Firestore fetch resolves) — read the same localStorage cache lastTopicId
    // itself already uses, one call earlier, rather than waiting on state that doesn't exist
    // yet. Falls back to Turkish when nothing's cached (first-ever visit on this device).
    const cachedLangCode = (load("profile")?.targetLanguage || "Turkish").toLowerCase();
    const curriculum = getCurriculum(cachedLangCode);
    const savedId = load("lastTopicId");
    if (savedId) return curriculum.find(t => t.topic_id === savedId) || curriculum[0];
    // Migrate old lastTopicOrder to lastTopicId on first load
    const savedOrder = load("lastTopicOrder");
    if (savedOrder) {
      const topic = curriculum.find(t => t.order === savedOrder) || curriculum[0];
      save("lastTopicId", topic.topic_id);
      save("lastTopicOrder", null);
      return topic;
    }
    return curriculum[0];
  });
  const setAndPersistTopic = (topic) => {
    setSelectedTopic(topic);
    save("lastTopicId", topic.topic_id);
    save("lastTopicOrder", null);
  };
  const [tokenUsage, setTokenUsage] = useState(() => load("tokenUsage") || { input: 0, output: 0, since: todayISO() });
  const [baranAlert, setBabiAlert] = useState(null);
  // True while AgentChat is waiting for the learner's free-text answer to the Babi Daily Study
  // Plan's "how much time do you have today" question — see fireDailyPlanQuestion below and
  // AgentChat.js's send() interception.
  const [pendingDailyPlan, setPendingDailyPlan] = useState(false);
  // "onboarding" (offered right after signup) or "settings" (retaken later) —
  // decides where PlacementTestView's onExit/onComplete send the user back to.
  const [placementOrigin, setPlacementOrigin] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [darkMode, setDarkMode] = useState(() => load("darkMode") === true);
  const [fontScale, setFontScale] = useState(() => {
    const s = load("fontScale");
    if (s === "large" || s === "xl") return s;
    if (load("fontLarge") === true) return "large"; // migrate old boolean
    return "normal";
  });

  const PADDLE_TOKEN             = process.env.REACT_APP_PADDLE_CLIENT_TOKEN       || "";
  const PADDLE_PRICE_PRO_MONTHLY = process.env.REACT_APP_PADDLE_PRICE_PRO_MONTHLY  || "";
  const PADDLE_PRICE_PRO_YEARLY  = process.env.REACT_APP_PADDLE_PRICE_PRO_YEARLY   || "";
  const PADDLE_ENV               = process.env.REACT_APP_PADDLE_ENV               || "";
  // A6: fresh-checkout price ids for a first-time subscriber choosing Elite directly (no
  // existing subscription to collide with, so a plain Paddle.Checkout.open() is correct here —
  // unlike an EXISTING Pro subscriber upgrading to Elite, which goes through the server-side
  // /api/subscription/change-tier route instead, see handleChangeVoiceTier below, specifically
  // to avoid risking a second, simultaneous subscription for someone who already has one).
  const PADDLE_PRICE_ELITE_MONTHLY = process.env.REACT_APP_PADDLE_PRICE_ELITE_MONTHLY || "";
  const PADDLE_PRICE_ELITE_YEARLY  = process.env.REACT_APP_PADDLE_PRICE_ELITE_YEARLY  || "";
  // One-time, non-subscription purchase — always a plain Paddle.Checkout.open(), regardless of
  // the buyer's current tier (Elite-only gate is enforced by canBuyVoiceOverage before this is
  // ever reachable, see AgentChat.js/SettingsView.js).
  const PADDLE_PRICE_VOICE_OVERAGE = process.env.REACT_APP_PADDLE_PRICE_VOICE_OVERAGE || "";

  const toggleDark = () => setDarkMode(d => !d);

  // True only between a checkout.completed event and the overlay actually
  // closing — lets checkout.closed tell "closed after a completed purchase"
  // apart from "closed without completing" (the only two ways to reach
  // checkout.closed). A ref, not state: it must be readable synchronously
  // inside the very next event callback without waiting on a re-render.
  const justPurchasedRef = useRef(false);

  // Initialize Paddle checkout once on mount
  useEffect(() => {
    if (!window.Paddle || !PADDLE_TOKEN || PADDLE_TOKEN.startsWith("REPLACE")) return;
    try {
      if (PADDLE_ENV === "sandbox") window.Paddle.Environment.set("sandbox");
      window.Paddle.Initialize({
        token: PADDLE_TOKEN,
        eventCallback: (event) => {
          if (event.name === "checkout.completed") {
            justPurchasedRef.current = true;
            // Optimistically mark as active; webhook will confirm and persist
            setSubscription(prev => ({ ...prev, status: "active" }));
          } else if (event.name === "checkout.closed") {
            // Only redirect when the close follows a completed purchase — a
            // checkout closed WITHOUT completing (user backs out) must keep
            // showing the pricing screen exactly as it does today.
            if (justPurchasedRef.current) {
              justPurchasedRef.current = false;
              setScreen("dashboard");
            }
          }
        },
      });
    } catch (e) { console.error("Paddle init failed:", e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    save("darkMode", darkMode);
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.classList.remove("font-large", "font-xl");
    if (fontScale === "large") document.documentElement.classList.add("font-large");
    else if (fontScale === "xl") document.documentElement.classList.add("font-xl");
    save("fontScale", fontScale);
  }, [fontScale]);

  useEffect(() => {
    const handler = () => {
      const t = load("tokenUsage") || { input: 0, output: 0, since: todayISO() };
      setTokenUsage(t);
      if (uidRef.current) saveUserData(uidRef.current, { tokenUsage: t });
    };
    window.addEventListener("tokenUpdate", handler);
    return () => window.removeEventListener("tokenUpdate", handler);
  }, []);

  const resetTokenUsage = async () => {
    const fresh = { input: 0, output: 0, since: todayISO() };
    save("tokenUsage", fresh);
    setTokenUsage(fresh);
    if (uidRef.current) await saveUserData(uidRef.current, { tokenUsage: fresh });
  };

  // Release up to 20 queued D2 cards per calendar day when entering Flashcards
  useEffect(() => {
    if (screen !== "flashcards" || !cards.length) return;
    const today = todayISO();
    if (load("lastD2Release") === today) return;
    save("lastD2Release", today);
    const toRelease = cards
      .filter(c => c.d2QueuedAt && !c.d2)
      .sort((a, b) => a.d2QueuedAt.localeCompare(b.d2QueuedAt))
      .slice(0, 20);
    if (!toRelease.length) return;
    const ids = new Set(toRelease.map(c => c.id));
    const updated = cards.map(c => ids.has(c.id) ? { ...c, d2: makeD2State() } : c);
    setCards(updated);
    save("flashcards", updated);
    if (user?.uid) saveUserData(user.uid, { flashcards: updated });
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const unsub = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        // Force-refresh: a role granted moments ago (e.g. via the admin app)
        // won't be on a cached ID token yet — same reasoning as the admin
        // app's own auth gate.
        const { claims } = await firebaseUser.getIdTokenResult(true);
        setIsAdminAccount(claims.role === "support_viewer" || claims.role === "full_admin");
        setIsSimpleViewer(claims.role === "simple_viewer");

        const isEmailUser = firebaseUser.providerData.some(p => p.providerId === "password");
        if (isEmailUser && !firebaseUser.emailVerified) {
          setUser(firebaseUser);
          setScreen("verify-email");
          return;
        }
        setUser(firebaseUser);
        uidRef.current = firebaseUser.uid;
        const data = await loadUserData(firebaseUser.uid);
        if (data?.profile) {
          // trialStartDate and frozenTopicIds live at the document root (server-managed).
          // Merge them into the local profile object so isPremiumActive can read them,
          // while saveUserData strips them before any write (they're never client-written).
          // Fall back to data.profile.* for backward compat with pre-migration documents.
          let prof = {
            ...data.profile,
            // Server-managed field (onUserCreated). Fall back to the account's Auth creation
            // time for a recent signup whose Cloud Function write hasn't landed yet, so a
            // brand-new account isn't misread as grandfathered — see resolveTrialStartDate.
            trialStartDate: resolveTrialStartDate(
              data.trialStartDate ?? data.profile?.trialStartDate,
              firebaseUser.metadata?.creationTime,
            ),
            frozenTopicIds: data.frozenTopicIds ?? data.profile?.frozenTopicIds ?? null,
          };
          // One-time backfill for accounts created before this field existed. Firebase Auth
          // already has a real, permanent creation timestamp for every account (new or old) —
          // used to gate the Babi Weekly Recap's "7 days of account history" check (see
          // babiCheckins.js accountAgeDays). Written back so this only ever runs once per account.
          if (!prof.accountCreatedAt && firebaseUser.metadata?.creationTime) {
            prof = { ...prof, accountCreatedAt: new Date(firebaseUser.metadata.creationTime).toISOString().slice(0, 10) };
            saveUserData(firebaseUser.uid, { profile: prof }).catch(() => {});
          }
          let prog = data.progress || defaultProgress();
          let rawCards = data.flashcards || [];
          let compTopics = data.completedTopics || {};

          // Ensure targetLanguage field exists on profile
          if (!prof.targetLanguage) {
            prof = { ...prof, targetLanguage: "Turkish" };
          }
          const tlCode = prof.targetLanguage.toLowerCase();

          if (!data.languages) {
            // One-time migration: create per-language archive for existing users
            const langData = {
              progress: prog, flashcards: rawCards, completedTopics: compTopics,
              targetWords: prof.targetWords || 3000,
              courseLengthDays: prof.courseLengthDays || 180,
              wordsPerDay: prof.wordsPerDay || (3000 / 180),
              startDate: prof.startDate || todayISO(),
              lastTopicId: load("lastTopicId") || null,
              lastTopicOrder: load("lastTopicOrder") || 1,
              grammarProgress: data.grammarProgress || {},
              placementTestLevel: prof.placementTestLevel || null,
              placementTestTakenAt: prof.placementTestTakenAt || null,
            };
            saveUserData(firebaseUser.uid, { profile: prof, languages: { [tlCode]: langData } }).catch(() => {});
          } else {
            if (!data.profile.targetLanguage) {
              saveUserData(firebaseUser.uid, { profile: prof }).catch(() => {});
            }
            // One-time backfill for accounts whose `languages` archive predates grammarProgress/
            // placementTestLevel joining the per-language swap: fold the existing flat top-level
            // values into the active language's own bucket — no re-keying of GP#### ids, they
            // just move one level down. Skipped once the bucket already has its own copy.
            const existingBucket = data.languages[tlCode];
            if (existingBucket && existingBucket.grammarProgress === undefined && existingBucket.placementTestLevel === undefined) {
              saveUserData(firebaseUser.uid, {
                languages: {
                  ...data.languages,
                  [tlCode]: {
                    ...existingBucket,
                    grammarProgress: data.grammarProgress || {},
                    placementTestLevel: prof.placementTestLevel || null,
                    placementTestTakenAt: prof.placementTestTakenAt || null,
                  },
                },
              }).catch(() => {});
            }
          }

          let gProg = data.grammarProgress || {};

          // Per-language data is used ONLY when top-level is empty (i.e., the user
          // just switched to this language and hasn't built any progress yet).
          // handleSwitchTargetLanguage already restores cards to top-level on switch,
          // so we must NOT override top-level with stale per-language data here.
          if (data.languages?.[tlCode] && !rawCards.length) {
            const ld = data.languages[tlCode];
            if (ld.flashcards) rawCards = ld.flashcards;
            if (ld.progress) prog = ld.progress;
            if (ld.completedTopics) compTopics = ld.completedTopics;
            // grammarProgress/placementTestLevel may be absent on a bucket archived before this
            // fix shipped — fall back to the pre-fix flat top-level value (safe today since only
            // Turkish exists; the backfill above makes this fallback permanent going forward).
            gProg = ld.grammarProgress ?? data.grammarProgress ?? {};
            prof = {
              ...prof,
              placementTestLevel: ld.placementTestLevel ?? prof.placementTestLevel ?? null,
              placementTestTakenAt: ld.placementTestTakenAt ?? prof.placementTestTakenAt ?? null,
            };
          }

          const migratedCards = rawCards.map(migrateSM2);
          setProfile(prof);
          setProgress(prog);
          setCards(migratedCards);
          setCompletedTopics(compTopics);
          save("profile", prof);
          save("progress", prog);
          save("flashcards", migratedCards);
          save("completedTopics", compTopics);

          // lastTopicId is a localStorage-only convenience and is cleared on every
          // sign-out — so a fresh sign-in (or a new device) has nothing to resume
          // from even when Firestore's completedTopics ledger shows real progress.
          // Derive a sensible starting point instead of silently defaulting to
          // Topic 1: the first topic not yet in the ledger. An empty ledger
          // naturally resolves to curriculum[0], which is still correct for a
          // genuinely new user.
          if (!load("lastTopicId")) {
            const curriculum = getCurriculum(tlCode);
            const nextIncomplete = curriculum.find(t => !compTopics[t.topic_id]) || curriculum[0];
            setAndPersistTopic(nextIncomplete);
          }
          setGrammarProgress(gProg);
          save("grammarProgress", gProg);
          const t = data.tokenUsage || { input: 0, output: 0, since: todayISO() };
          save("tokenUsage", t);
          setTokenUsage(t);
          setSubscription(data.subscription || null);

          // Teacher Partner Terms acceptance — only relevant for teachers, and only once the
          // Terms are out of DRAFT. Loaded off the backend audit record, never a profile flag.
          // Fired without blocking routing; the Home banner / dashboard line react when it lands.
          if (isTeacher(prof) && !TEACHER_TERMS_DRAFT) {
            loadTeacherTermsAcceptance(firebaseUser.uid, TEACHER_TERMS_VERSION)
              .then((rec) => setTeacherTermsAccepted(rec))
              .catch(() => setTeacherTermsAccepted(null));
          } else {
            setTeacherTermsAccepted(null);
          }

          // Honour a direct visit to /teacher-terms or /teacher-dashboard (bookmark / shared
          // link / the public page's "log in to accept" button). A learner who lands on either
          // is bounced back to the dashboard by that screen's own role guard.
          setScreen(
            teacherTermsDeepLink ? "teacherTerms"
              : teacherDashDeepLink ? "teacherDashboard"
                : "dashboard"
          );
        } else {
          // New user — check localStorage for migration
          const localProfile = load("profile");
          if (localProfile) {
            const localProgress = load("progress") || defaultProgress();
            const localCards = (load("flashcards") || []).map(migrateSM2);
            const localCompletedTopics = load("completedTopics") || {};
            const localTokens = load("tokenUsage") || { input: 0, output: 0, since: todayISO() };
            await saveUserData(firebaseUser.uid, { profile: localProfile, progress: localProgress, flashcards: localCards, completedTopics: localCompletedTopics, tokenUsage: localTokens });
            setProfile(localProfile); setProgress(localProgress); setCards(localCards); setCompletedTopics(localCompletedTopics);
            setScreen("dashboard");
          } else {
            setScreen("onboarding");
          }
        }
      } else {
        // Clear user-specific localStorage so a new sign-in never picks up a previous user's cached data
        ["profile","progress","flashcards","completedTopics","grammarProgress","tokenUsage","lastTopicId","lastTopicOrder","lastD2Release","d2Explained","lastLessonPosition"].forEach(k => save(k, null));
        setUser(null);
        setIsAdminAccount(false);
        uidRef.current = null;
        setProfile(null); setProgress(null); setCards([]); setCompletedTopics({}); setGrammarProgress({});
        setScreen("signin");
      }
    });
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the address bar in step with the two screens that have a real Hosting path,
  // /teacher-dashboard and /teacher-terms, so a teacher can bookmark or share them. Deliberately
  // minimal: a replaceState (no history entry) in each direction, and nothing at all while auth
  // is still resolving — the initial deep-link decision is already captured at mount.
  useEffect(() => {
    if (screen === "loading") return;
    const PATHS = { teacherDashboard: "/teacher-dashboard", teacherTerms: "/teacher-terms" };
    const wantPath = PATHS[screen] || null;
    const onAKnownPath = Object.values(PATHS).includes(window.location.pathname);
    if (wantPath && window.location.pathname !== wantPath) {
      window.history.replaceState(null, "", wantPath);
    } else if (!wantPath && onAKnownPath) {
      window.history.replaceState(null, "", "/");
    }
  }, [screen]);

  // When trial expiry is detected, ask the server to verify and write frozenTopicIds.
  // The server uses its own trusted trialStartDate (set by the onCreate Cloud Function),
  // so this is not bypassable by client clock manipulation or direct Firestore edits.
  // trialTs is a stable primitive derived from the server timestamp, so the effect
  // re-runs once per sign-in (when Firestore data loads) and not on every render.
  // Also depends on targetLanguage: computeFreeze is now per-language (functions/index.js),
  // and handleSwitchTargetLanguage sets frozenTopicIds to null on switching to a language with
  // no archived snapshot yet — without targetLanguage in the deps, this effect wouldn't re-fire
  // just because that happened (none of trialTs/subscription/uid change on a language switch),
  // leaving a freshly-switched-to language never actually checked for the rest of the session.
  const trialTs = profile?.trialStartDate?.seconds ?? profile?.trialStartDate ?? null;
  useEffect(() => {
    if (!profile || !user?.uid) return;
    if (subscription?.status === "active") return;
    if (!trialTs) return; // grandfathered user — no trial to check
    if (profile.frozenTopicIds != null) return; // already frozen (or already restored) for this language
    computeFreeze()
      .then(result => {
        if (result.frozen) {
          const ids = result.frozenTopicIds || [];
          setProfile(prev => {
            if (prev.frozenTopicIds != null) return prev; // already set by a concurrent update
            const next = { ...prev, frozenTopicIds: ids };
            save("profile", next);
            return next;
          });
        }
      })
      .catch(() => {}); // silent fail — frozen users see all topics locked until next load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialTs, subscription?.status, user?.uid, profile?.targetLanguage]);

  // Babi proactive check-ins (dev backlog task 12's never-built "first open of the day" moment,
  // finally built here): at most one of Weekly Recap / Daily Study Plan fires per calendar day.
  const babiCheckinFiredRef = useRef(false);
  useEffect(() => {
    if (!profile || !progress || !user?.uid) return;
    if (babiCheckinFiredRef.current) return;
    if (["loading", "signin", "verify-email", "onboarding", "placementTest"].includes(screen)) return;
    babiCheckinFiredRef.current = true;
    runBabiCheckin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, progress, user, screen]);

  const runBabiCheckin = async () => {
    const today = todayISO();
    if (progress.lastBabiPopupDate === today) return;
    // First-ever check-in: AgentChat's own "chat.welcome" first-open message already
    // greets and orients a brand-new user, so skip the daily-plan nudge here today —
    // stacking "how much time do you have?" right behind that welcome felt abrupt.
    if (!progress.lastBabiPopupDate) {
      const updProg = { ...progress, lastBabiPopupDate: today, lastBabiPopupType: "welcome" };
      setProgress(updProg);
      save("progress", updProg);
      if (user?.uid) saveUserData(user.uid, { progress: updProg }).catch(() => {});
      return;
    }
    // Settings toggles (default on — undefined means "never touched, still enabled").
    const recapEnabled = profile?.babiWeeklyReportEnabled ?? true;
    const dailyNudgeEnabled = profile?.babiDailyNudgeEnabled ?? true;
    const { weekStart, weekEnd } = getPriorCompleteWeek(today);
    const eligibleForRecap = recapEnabled && accountAgeDays(profile, today) >= 7 && progress.lastRecapWeekStart !== weekStart;
    if (eligibleForRecap) await fireWeeklyRecap(weekStart, weekEnd, today);
    else if (dailyNudgeEnabled) fireDailyPlanQuestion(today);
  };

  const fireWeeklyRecap = async (weekStart, weekEnd, today) => {
    // Persist the dedup fields BEFORE the Haiku call resolves — a slow/failed API call must
    // never leave the popup re-eligible to fire again on the next render/reload today.
    const updProg = { ...progress, lastBabiPopupDate: today, lastBabiPopupType: "recap", lastRecapWeekStart: weekStart };
    setProgress(updProg);
    save("progress", updProg);
    if (user?.uid) saveUserData(user.uid, { progress: updProg }).catch(() => {});
    try {
      const stats = computeWeeklyRecapStats(profile, progress, cards, weekStart, weekEnd);
      const text = await phraseWeeklyRecap(stats, profile?.baseLanguage || "English");
      setBabiAlert(text);
    } catch (_) {
      setBabiAlert({ key: "chat.weeklyRecapFallback", params: { streak: progress.streak || 0 } });
    }
  };

  const fireDailyPlanQuestion = (today) => {
    const updProg = { ...progress, lastBabiPopupDate: today, lastBabiPopupType: "plan" };
    setProgress(updProg);
    save("progress", updProg);
    if (user?.uid) saveUserData(user.uid, { progress: updProg }).catch(() => {});
    const qIdx = Math.floor(Math.random() * 3) + 1;
    setPendingDailyPlan(true);
    setBabiAlert({ key: `chat.dailyPlanQuestion${qIdx}` });
  };

  const handleDailyPlanAnswered = () => setPendingDailyPlan(false);

  const handleOnboardingComplete = async (prof, consents = {}) => {
    const profWithLang = {
      ...prof,
      targetLanguage: prof.targetLanguage || "Turkish",
      // The authoritative trialStartDate is stamped server-side by the onUserCreated Cloud
      // Function; saveUserData strips this local value before every write. It only exists in
      // React state so this just-created account gates as "trial in progress" (10-min voice
      // allotment, etc.) during the session before that server value first loads — without it
      // the account reads as grandfathered. Replaced by the real server value on next load.
      trialStartDate: user?.metadata?.creationTime
        ? new Date(user.metadata.creationTime).toISOString()
        : new Date().toISOString(),
      consentMarketing: consents.consentMarketing ?? false,
      consentDate: new Date().toISOString(),
      // Reserved for a future "how did you hear about us?" onboarding
      // question — no UI/prompt yet, just the field slot so nothing needs
      // restructuring later.
      referralSource: null,
      accountCreatedAt: todayISO(),
    };
    const prog = defaultProgress();
    const tlCode = profWithLang.targetLanguage.toLowerCase();
    const langData = {
      progress: prog, flashcards: [], completedTopics: {},
      targetWords: profWithLang.targetWords || 3000,
      courseLengthDays: profWithLang.courseLengthDays || 180,
      wordsPerDay: profWithLang.wordsPerDay || (3000 / 180),
      startDate: profWithLang.startDate || todayISO(),
      lastTopicId: getCurriculum(tlCode)[0].topic_id,
      lastTopicOrder: 1,
    };
    setProfile(profWithLang); setProgress(prog); setCards([]); setCompletedTopics({});
    save("profile", profWithLang); save("progress", prog); save("flashcards", []); save("completedTopics", {});
    if (user?.uid) await saveUserData(user.uid, { profile: profWithLang, progress: prog, flashcards: [], completedTopics: {}, languages: { [tlCode]: langData } });
    setPlacementOrigin("onboarding");
    setScreen("placementTest");
  };

  // Shared by both the "Not Now"/skip path and the placement-test completion
  // path, so a new user reaches the same install-prompt-or-dashboard decision
  // whether or not they took the test.
  const goToPostOnboardingScreen = () => {
    const isMobile     = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isInstalled  = window.matchMedia("(display-mode: standalone)").matches || !!window.navigator.standalone;
    const alreadySeen  = !!load("installPromptShown");
    setScreen(isMobile && !isInstalled && !alreadySeen ? "install-prompt" : "dashboard");
  };

  const handlePlacementTestExit = () => {
    if (placementOrigin === "onboarding") goToPostOnboardingScreen();
    else setScreen("goals");
    setPlacementOrigin(null);
  };

  // Called once, when PlacementTestView reaches its results screen — applies
  // the placement (starting topic + profile level) right away. The test only
  // measures level; it never creates flashcards — words enter FSRS through
  // their lessons. handlePlacementTestExit (bound to the results screen's
  // Continue button) only handles navigation after that.
  const handlePlacementTestComplete = async (result) => {
    const updatedProfile = { ...profile, placementTestLevel: result.level, placementTestTakenAt: todayISO() };
    setProfile(updatedProfile);
    setAndPersistTopic(result.startTopic);
    save("profile", updatedProfile);
    if (user?.uid) {
      try { await saveUserData(user.uid, { profile: updatedProfile }); }
      catch (e) { console.error("Placement test save failed:", e); }
    }
  };

  const handleLessonComplete = async (newCards, minutes) => {
    // Safety net: frozen users cannot complete topics outside their frozen set.
    if (!isPremiumActive(profile, subscription, isAdminAccount, isSimpleViewer) && profile?.frozenTopicIds) {
      const tid = selectedTopic?.topic_id;
      if (tid && !profile.frozenTopicIds.includes(tid)) { setScreen("dashboard"); return; }
    }
    const today = todayISO();
    const newWords = newCards.length;
    const updProg = { ...progress };
    updProg.wordsLearned += newWords;
    updProg.lessonsCompleted += 1;
    updProg.totalMinutes += minutes;
    updProg.lastStudy = today;
    const todayLog = updProg.dailyLog.find(l => l.date === today);
    if (todayLog) { todayLog.words += newWords; todayLog.minutes += minutes; todayLog.lessons = (todayLog.lessons || 0) + 1; }
    else updProg.dailyLog.push({ date: today, words: newWords, minutes, lessons: 1 });
    const streakResult = computeStreakUpdate(progress.streak, progress.protectionDays, progress.lastStudy, today);
    updProg.streak = streakResult.streak;
    updProg.protectionDays = streakResult.newProtectionDays;
    if (streakResult.missedDays > 0) {
      setBabiAlert(streakResult.wiped
        ? { key: "chat.streakWiped", params: { days: streakResult.missedDays, protection: streakResult.protectionDays } }
        : { key: "chat.streakDecayed", params: { days: streakResult.missedDays, protection: streakResult.protectionDays, oldStreak: progress.streak, newStreak: streakResult.streak } });
    }
    if (updProg.streak > (progress.longestStreak || 0)) {
      updProg.longestStreak = updProg.streak;
      updProg.longestStreakAt = today;
    }
    const allCards = [...cards, ...newCards];
    const d = cardDist(allCards);
    const logEntry = updProg.dailyLog.find(l => l.date === today);
    if (logEntry) { logEntry.fluent = d[2]; logEntry.retained = d[3]; }
    // Explicit completion ledger write — never derived from cards, so a
    // recycled word's card can't imply topic completion and a topic rename
    // can't break it (keyed by stable topic_id, not by name).
    const completedTid = selectedTopic?.topic_id;
    const updCompletedTopics = (completedTid && !completedTopics[completedTid])
      ? { ...completedTopics, [completedTid]: { completedAt: Date.now() } }
      : completedTopics;

    setProgress(updProg); setCards(allCards); setCompletedTopics(updCompletedTopics);
    save("progress", updProg); save("flashcards", allCards); save("completedTopics", updCompletedTopics);
    if (user?.uid) await saveUserData(user.uid, { progress: updProg, flashcards: allCards, completedTopics: updCompletedTopics });

    // Advance "Current Lesson" to the next topic so it doesn't re-open the one
    // just finished. Mirrors AllLessonsView's own completedTopics/overwhelm-gate
    // logic (App.js AllLessonsView) so this can't route someone past the same gate
    // that view enforces. If there's no next topic, or the gate would lock it,
    // leave selectedTopic on the just-completed topic (today's existing behavior).
    const totalDue = allCards.filter(c =>
      (!c.d1Mastered && c.due <= today) || (c.d2 && !c.d2Mastered && c.d2.due <= today)
    ).length;
    const overwhelmed = totalDue > 60;
    // topic_order is NOT guaranteed to be a gapless integer sequence — inserting a topic between
    // two existing ones is done via a fractional order value (e.g. 10.5) rather than renumbering
    // everything after it, so an exact `order === current + 1` match can silently skip a topic.
    // Always find the next topic by sort order instead of by arithmetic.
    const currentOrder = selectedTopic?.order || 0;
    const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();
    const nextTopic = getCurriculum(langCode).filter(t => t.order > currentOrder).sort((a, b) => a.order - b.order)[0];
    if (nextTopic && !(overwhelmed && !updCompletedTopics[nextTopic.topic_id])) {
      setAndPersistTopic(nextTopic);
    }

    setScreen("dashboard");
  };

  const handleCardReview = async (reviewed, { toDashboard = true, minutes = 0 } = {}) => {
    const correct = reviewed.filter(c => c._rating >= 3).length;
    // A single review session/save is always one deck's queue (FlashcardView's startDeck builds
    // the pool from either d1Due or d2Due, never both), so every entry here shares the same
    // _deck — safe to read it off the first item to log this batch's time under the right deck.
    const isD2Batch = reviewed.length > 0 && reviewed[0]._deck === 2;
    // Separate D1 and D2 reviewed entries — same card can appear in both decks
    const d1Map = {}, d2Map = {};
    for (const c of reviewed) {
      const { _rating, _deck, ...card } = c;
      if (_deck === 2) d2Map[card.id] = card.d2;
      else d1Map[card.id] = card;
    }
    const today = todayISO();
    const updated = cards.map(c => {
      let out = c;
      if (d1Map[c.id]) {
        out = { ...out, ...d1Map[c.id] };
        if (out.stability >= 21 && !out.d2QueuedAt)
          out = { ...out, d2QueuedAt: new Date().toISOString() };
        // d1MasteredAt is stamped at the exact moment of the flip (new instrumentation for
        // the Babi Weekly Recap's "retention wins" bullet — see computeWeeklyRecapStats),
        // so only a genuine within-window flip is ever surfaced, never inferred after the fact.
        if (out.stability > 365 && !out.d1Mastered)
          out = { ...out, d1Mastered: true, d1MasteredAt: today };
      }
      if (d2Map[c.id]) {
        out = { ...out, d2: d2Map[c.id] };
        if (out.d2?.stability > 365 && !out.d2Mastered)
          out = { ...out, d2Mastered: true, d2MasteredAt: today };
      }
      return out;
    });
    const updProg = { ...progress };
    updProg.lastStudy = today;
    updProg.totalMinutes = (updProg.totalMinutes || 0) + minutes;
    const d = cardDist(updated);
    const log = updProg.dailyLog.find(l => l.date === today);
    if (log) {
      log.accuracy = ((log.accuracy || 0.8) + correct / Math.max(reviewed.length, 1)) / 2;
      log.minutes = (log.minutes || 0) + minutes;
      log.fluent = d[2]; log.retained = d[3];
      log.reviews = (log.reviews || 0) + reviewed.length;
      // D1-only time/count, kept separate from the blended totals above so the Daily Study
      // Plan's per-card pace estimate (deriveSecondsPerCard, babiCheckins.js) can be derived
      // from D1 (the slower, listen+recall+reveal deck) without D2's much quicker flip-and-rate
      // sessions dragging the average down — see the "131 cards in 9 minutes" bug this fixed.
      if (!isD2Batch) {
        log.d1Minutes = (log.d1Minutes || 0) + minutes;
        log.d1Reviews = (log.d1Reviews || 0) + reviewed.length;
      }
    } else if (minutes > 0) {
      // Flashcard-only day: no lesson-completion entry exists yet for today, so create one
      // purely to record the time spent — otherwise a pure-review day would vanish from the
      // daily log entirely and skew any day-by-day average toward zero.
      updProg.dailyLog.push({
        date: today, words: 0, lessons: 0, minutes,
        accuracy: correct / Math.max(reviewed.length, 1),
        fluent: d[2], retained: d[3], reviews: reviewed.length,
        ...(isD2Batch ? {} : { d1Minutes: minutes, d1Reviews: reviewed.length }),
      });
    }
    const streakResult = computeStreakUpdate(progress.streak, progress.protectionDays, progress.lastStudy, today);
    updProg.streak = streakResult.streak;
    updProg.protectionDays = streakResult.newProtectionDays;
    if (streakResult.missedDays > 0) {
      setBabiAlert(streakResult.wiped
        ? { key: "chat.streakWiped", params: { days: streakResult.missedDays, protection: streakResult.protectionDays } }
        : { key: "chat.streakDecayed", params: { days: streakResult.missedDays, protection: streakResult.protectionDays, oldStreak: progress.streak, newStreak: streakResult.streak } });
    }
    if (updProg.streak > (progress.longestStreak || 0)) {
      updProg.longestStreak = updProg.streak;
      updProg.longestStreakAt = today;
    }
    setCards(updated); setProgress(updProg);
    save("flashcards", updated); save("progress", updProg);
    if (user?.uid) {
      await saveUserData(user.uid, { progress: updProg, flashcards: updated });
      // Best-effort: never let a logging failure block the real save above.
      logReviewEvents(user.uid, reviewed.map(c => ({ cardId: c.id, deck: c._deck, rating: c._rating }))).catch(() => {});
    }
    if (toDashboard) setScreen("dashboard");
  };

  // Listen & Speak Lab session completion. Interpretation B: any session that reaches the
  // "ended" phase with at least one full learner exchange counts as a day's practice — the
  // same low bar as saving a single flashcard review. Mirrors handleCardReview's streak
  // handling exactly (computeStreakUpdate is unchanged); the only extra is a dailyLog entry
  // so the day still shows on the Progress page even though no words/lessons happened.
  // ListenSpeakLab.js guarantees this fires at most once per session and only when
  // learnerTurns >= 1, so there is no gate to re-check here — also why labSessionsCompleted
  // (§7) is incremented unconditionally below rather than re-checking that gate.
  // The state-transition itself lives in applyLabSessionCompletion (utils.js) — pulled out so
  // it's unit-testable (App.js can't be imported by Jest, see that function's own comment) —
  // this wrapper only owns the side effects: the streak-alert banner, setProgress, persistence.
  // grammarTally — { [grammar_point_id]: count } from computeGrammarTally (ListenSpeakLab.js),
  // already null-filtered there — merges into progress.labGrammarTally, nested inside `progress`
  // (not top-level like grammarProgress) specifically so it rides progress's existing per-
  // language archive/restore swap in handleSwitchTargetLanguage below.
  const handleLabSessionComplete = async ({ learnerTurns = 0, minutes = 0, grammarTally = {} } = {}) => {
    const today = todayISO();
    const { updProg, streakResult } = applyLabSessionCompletion(progress, { learnerTurns, minutes, grammarTally }, today);
    if (streakResult.missedDays > 0) {
      setBabiAlert(streakResult.wiped
        ? { key: "chat.streakWiped", params: { days: streakResult.missedDays, protection: streakResult.protectionDays } }
        : { key: "chat.streakDecayed", params: { days: streakResult.missedDays, protection: streakResult.protectionDays, oldStreak: progress.streak, newStreak: streakResult.streak } });
    }
    setProgress(updProg);
    save("progress", updProg);
    if (user?.uid) await saveUserData(user.uid, { progress: updProg });
  };

  // Independent grammar-review cadence (NOT FSRS, NOT the flashcard overwhelm gate).
  // `entry` is the already-computed next cadence state for this grammar point.
  // Merged (not replaced) so the `viewed` flag set by handleMarkGrammarPointViewed
  // survives a subsequent practice session.
  const handleReviewGrammarPoint = async (grammarPointId, entry) => {
    const updated = { ...grammarProgress, [grammarPointId]: { ...grammarProgress?.[grammarPointId], ...entry } };
    setGrammarProgress(updated);
    save("grammarProgress", updated);
    if (user?.uid) await saveUserData(user.uid, { grammarProgress: updated });
  };

  // Simple "has this point ever been opened" flag — separate from the
  // spaced-repetition cadence — drives the per-card Reviewed/Not Reviewed badge.
  const handleMarkGrammarPointViewed = async (grammarPointId) => {
    if (grammarProgress?.[grammarPointId]?.viewed) return;
    const updated = { ...grammarProgress, [grammarPointId]: { ...grammarProgress?.[grammarPointId], viewed: true } };
    setGrammarProgress(updated);
    save("grammarProgress", updated);
    if (user?.uid) await saveUserData(user.uid, { grammarProgress: updated });
  };

  const handleSaveGoals = async (prof) => {
    setProfile(prof); save("profile", prof);
    // Use auth.currentUser (live auth state) instead of React's user state, which can be
    // stale when GoalsView's unmount cleanup fires during sign-out.
    if (auth?.currentUser?.uid) await saveUserData(auth.currentUser.uid, { profile: prof });
  };

  const handleSwitchTargetLanguage = async (newLang) => {
    const currentLangCode = (profile?.targetLanguage || "Turkish").toLowerCase();
    const newLangCode = newLang.toLowerCase();
    if (currentLangCode === newLangCode || !user?.uid) return;

    // Archive current language's live state
    const currentLangData = {
      progress,
      flashcards: cards,
      completedTopics,
      targetWords: profile.targetWords,
      courseLengthDays: profile.courseLengthDays,
      wordsPerDay: profile.wordsPerDay,
      startDate: profile.startDate,
      lastTopicId: selectedTopic?.topic_id || null,
      lastTopicOrder: selectedTopic?.order || 1,
      grammarProgress,
      placementTestLevel: profile.placementTestLevel || null,
      placementTestTakenAt: profile.placementTestTakenAt || null,
    };

    // Fetch stored data for new language (may not exist yet)
    const userData = await loadUserData(user.uid);
    const newLangStored = userData?.languages?.[newLangCode];

    const newProg = newLangStored?.progress || defaultProgress();
    const newCards = (newLangStored?.flashcards || []).map(migrateSM2);
    const newCompletedTopics = newLangStored?.completedTopics || {};
    const newTargetWords = newLangStored?.targetWords ?? 3000;
    const newCourseLengthDays = newLangStored?.courseLengthDays ?? 180;
    const newWordsPerDay = newLangStored?.wordsPerDay ?? (newTargetWords / Math.max(1, newCourseLengthDays));
    const newStartDate = newLangStored?.startDate || todayISO();
    const newLastTopicId = newLangStored?.lastTopicId || null;
    const newLastTopicOrder = newLangStored?.lastTopicOrder || 1;
    const newGrammarProgress = newLangStored?.grammarProgress || {};

    const newProfile = {
      ...profile,
      targetLanguage: newLang,
      targetWords: newTargetWords,
      courseLengthDays: newCourseLengthDays,
      wordsPerDay: newWordsPerDay,
      startDate: newStartDate,
      placementTestLevel: newLangStored?.placementTestLevel || null,
      placementTestTakenAt: newLangStored?.placementTestTakenAt || null,
      // frozenTopicIds is restored (never archived) here on purpose: it's server-managed —
      // computeFreeze (functions/index.js) is the only writer, at both the flat top-level
      // field and languages.{langCode}.frozenTopicIds — and saveUserData strips any client
      // attempt to write it at either path. currentLangData above deliberately omits it for
      // the same reason: the server already owns whatever's archived for currentLangCode.
      frozenTopicIds: newLangStored?.frozenTopicIds ?? null,
    };

    // Persist: archive current lang, activate new lang
    const allLanguages = { ...(userData?.languages || {}), [currentLangCode]: currentLangData };
    await saveUserData(user.uid, {
      profile: newProfile,
      progress: newProg,
      flashcards: newCards,
      completedTopics: newCompletedTopics,
      grammarProgress: newGrammarProgress,
      languages: allLanguages,
    });

    setProfile(newProfile);
    setProgress(newProg);
    setCards(newCards);
    setCompletedTopics(newCompletedTopics);
    setGrammarProgress(newGrammarProgress);
    save("profile", newProfile);
    save("progress", newProg);
    save("flashcards", newCards);
    save("completedTopics", newCompletedTopics);
    save("grammarProgress", newGrammarProgress);

    const newCurriculum = getCurriculum(newLangCode);
    const newTopic = (newLastTopicId && newCurriculum.find(t => t.topic_id === newLastTopicId))
      || newCurriculum.find(t => t.order === newLastTopicOrder)
      || newCurriculum[0];
    setAndPersistTopic(newTopic);
  };

  // A6: `tier` ("pro" | "elite") lets a first-time subscriber (no existing subscription at
  // all) choose Elite directly from PaywallScreen, not just Pro — a fresh checkout either way,
  // so there's no double-subscription risk regardless of which tier is picked. Defaults to
  // "pro" so every pre-existing call site that only ever passed `billing` keeps working
  // unchanged (PaywallScreen's CTA is the only one that ever passes anything at all; every
  // other onUpgrade() caller across the app just navigates to the plan-selector screen).
  const handleUpgrade = (billing, tier = "pro") => {
    // No billing arg = show plan selector screen first
    if (!billing) {
      setScreen("upgrade");
      return;
    }
    // Duplicate-purchase guard: never open a second checkout for an already-
    // active subscriber, even if some stale UI state still called this with
    // a billing arg (PaywallScreen's own "already Pro" branch above should
    // already prevent the CTA from existing, but this is the actual
    // checkout-opening chokepoint, so it guards independently of that). This
    // screen is reachable only pre-subscription, so it's never the right place
    // for an existing Pro subscriber to reach Elite — that's a tier CHANGE
    // (Settings > Upgrade to Elite), not a fresh checkout.
    if (subscription?.status === "active") {
      setScreen("dashboard");
      return;
    }
    // billing arg provided = open Paddle checkout for that plan
    if (!window.Paddle || !PADDLE_TOKEN || PADDLE_TOKEN.startsWith("REPLACE")) {
      window.open("https://speafy.com/pricing", "_blank");
      return;
    }
    const priceId = tier === "elite"
      ? (billing === "yearly" ? PADDLE_PRICE_ELITE_YEARLY : PADDLE_PRICE_ELITE_MONTHLY)
      : (billing === "yearly" ? PADDLE_PRICE_PRO_YEARLY   : PADDLE_PRICE_PRO_MONTHLY);
    const checkoutParams = {
      items: [{ priceId, quantity: 1 }],
      customData: { uid: user?.uid || "" },
    };
    // Step 19 of the affiliate roadmap: carry the captured referral code (if any) forward into
    // Paddle's custom_data, so it rides along on every webhook event for the resulting
    // subscription — the webhook (step 20) reads it from there to write profile.referredBy +
    // the first commissionEntries doc. getReferralCodeForCheckout also refuses a self-referral.
    // Only the real subscription checkout needs this — handleBuyVoiceOverage below is a one-time
    // purchase with no subscription_id at all, so it can never earn commission (see the ledger
    // schema: only subscription-linked transaction.completed events produce an entry).
    const referralCode = getReferralCodeForCheckout(user?.uid);
    if (referralCode) checkoutParams.customData.ref = referralCode;
    if (user?.email) checkoutParams.customer = { email: user.email };
    window.Paddle.Checkout.open(checkoutParams);
  };

  // Both of these only trigger the real Paddle API call server-side — the
  // `subscription` field itself is updated later by the webhook, never
  // written directly here (see proxy/index.js and firestore.rules).
  const handlePause = () => pauseSubscription();
  const handleResume = () => resumeSubscription();

  // A6: overage-minutes top-up — a plain one-time-price Paddle checkout, safe to open even for
  // an existing subscriber (unlike a tier change) since it's not a subscription at all. No
  // duplicate-purchase guard needed for the same reason. Firestore's voiceBalance.purchasedSeconds
  // is credited later by the webhook (see proxy/index.js), never written here.
  //
  // `hours` defaults to 1 — there's no quantity-picker UI today (every call site just triggers a
  // single 1-hour block), but this is written to accept one so a future UI addition inherits the
  // cap automatically. The real, authoritative enforcement of the 20-hour max is Paddle's own
  // price-level quantity.maximum, configured directly on the voice-overage price in Paddle's
  // dashboard — confirmed (not assumed) that Paddle rejects a checkout above a price's configured
  // quantity range server-side, independent of anything this client sends. This clamp is
  // defense-in-depth / better UX (fails fast with no wasted round trip to Paddle), not itself the
  // security boundary.
  const handleBuyVoiceOverage = (hours = 1) => {
    if (!window.Paddle || !PADDLE_TOKEN || PADDLE_TOKEN.startsWith("REPLACE") || !PADDLE_PRICE_VOICE_OVERAGE) {
      window.open("https://speafy.com/pricing", "_blank");
      return;
    }
    const quantity = Math.min(Math.max(1, Math.round(hours) || 1), VOICE_OVERAGE_MAX_HOURS_PER_PURCHASE);
    const checkoutParams = {
      items: [{ priceId: PADDLE_PRICE_VOICE_OVERAGE, quantity }],
      customData: { uid: user?.uid || "" },
    };
    if (user?.email) checkoutParams.customer = { email: user.email };
    window.Paddle.Checkout.open(checkoutParams);
  };

  // A6: Basic<->Elite tier change on an existing subscription — server-side call, not a client
  // checkout (see proxy/index.js's /api/subscription/change-tier for why). Just reports request
  // success/failure; `subscription.tier`/`pendingTier` land via the webhook or (for a deferred
  // downgrade) the route's own synchronous write, same "fire the request, state follows shortly
  // after" contract as handlePause/handleResume above.
  const handleChangeVoiceTier = (targetTier) => changeVoiceTier(targetTier);

  // `updates` is normally a plain object, merged as before. It may also be a function
  // (prev => partialUpdates) for callers that can't safely rely on a `profile` value
  // captured in a closure from earlier in a long-running async chain — e.g. A5's voice-
  // balance billing, which can call this twice in one voice turn (once right after STT,
  // once later after TTS finishes) from closures fixed back when mic capture began. A
  // plain-object update reading a stale `profile` snapshot would silently lose whichever
  // bill applied first once the second call overwrote it. setProfile's own functional-
  // updater form is what actually guarantees each call sees the true latest state, not
  // just accepting a function argument here — the Promise wrapper below exists only to
  // make the merged result available synchronously-after for the save()/saveUserData()
  // calls, not for correctness of the merge itself.
  const handleUpdateProfile = async (updates) => {
    let updated;
    await new Promise(resolve => {
      setProfile(prev => {
        const partial = typeof updates === "function" ? updates(prev) : updates;
        updated = { ...prev, ...partial };
        resolve();
        return updated;
      });
    });
    save("profile", updated);
    if (user?.uid) {
      try { await saveUserData(user.uid, { profile: updated }); }
      catch (e) { console.error("Profile update failed:", e); }
    }
  };

  // The acceptTeacherPartnerTerms callable succeeded (fresh acceptance or an already-existing
  // record). Store it so the Home banner and dashboard status line update immediately, and take
  // the teacher to their dashboard.
  const handleAcceptedTerms = (rec) => {
    setTeacherTermsAccepted(rec || { termsVersion: TEACHER_TERMS_VERSION, acceptedAt: new Date().toISOString() });
    setScreen("teacherDashboard");
  };

  const handleSignOut = async () => {
    save("profile", null); save("progress", null); save("flashcards", null);
    save("tokenUsage", null);
    setSubscription(null);
    await signOutUser();
  };

  // Wipes learning progress only — never signs the user out or touches profile
  // identity/settings (name, baseLanguage, targetLanguage, country, goals, consent).
  // `profile: null` used to be how this cleared things, but onAuthChange treats a
  // null profile as "brand-new user" (see the `if (data?.profile)` branch above),
  // which silently dropped a reset account back into onboarding — profile now
  // survives entirely untouched.
  const handleReset = async () => {
    const freshProgress = defaultProgress();

    setProgress(freshProgress);
    setCards([]);
    setCompletedTopics({});
    setGrammarProgress({});
    setAndPersistTopic(getCurriculum((profile?.targetLanguage || "Turkish").toLowerCase())[0]);

    save("progress", freshProgress);
    save("flashcards", []);
    save("completedTopics", {});
    save("grammarProgress", {});
    save("welcomeShown", null);
    save(LESSON_POSITION_KEY, null);
    await resetTokenUsage();

    if (!user?.uid) return;

    // The per-target-language archive (`languages`) holds its own copy of
    // progress/flashcards/completedTopics/grammarProgress for every language the learner has
    // ever switched to (see handleSwitchTargetLanguage). Danger Zone reset is scoped to the
    // language actually being reset — it must never touch another language's archived data
    // just because it lives in the same `languages` map, and the active language's OWN archive
    // entry still needs the same fields cleared as its top-level copy, or a later sign-out/
    // sign-in would fall back to this archive (onAuthChange's `!rawCards.length` branch) and
    // resurrect the pre-reset data.
    const tlCode = (profile?.targetLanguage || "Turkish").toLowerCase();
    const userData = await loadUserData(user.uid);
    const resetLanguages = userData?.languages
      ? Object.fromEntries(Object.entries(userData.languages).map(([code, entry]) => [
          code,
          code === tlCode
            ? { ...entry, progress: freshProgress, flashcards: [], completedTopics: {}, targetWords: 3000, grammarProgress: {} }
            : entry,
        ]))
      : {};

    await saveUserData(user.uid, {
      progress: freshProgress,
      flashcards: [],
      completedTopics: {},
      grammarProgress: {},
      languages: resetLanguages,
    });
  };

  const handleDeleteAccount = async (password) => {
    ["profile","progress","flashcards","welcomeShown","tokenUsage","grammarProgress"].forEach(k => save(k, null));
    await deleteAccount(password);
    // onAuthStateChanged fires → setScreen("signin") automatically
  };

  // Special-cased ahead of the "loading"/screen state machine below: a visitor
  // clicking a Firebase email-verification or password-reset link is not yet in
  // a normal authenticated app session (onAuthChange above is still resolving,
  // or they aren't signed in at all), so they must never be routed through
  // onboarding/sign-in gating first. AuthActionScreen manages its own
  // loading/success/error states independently of `screen`.
  //
  // baseLanguage here mirrors the normal branch's `profile?.baseLanguage ||
  // "English"` below rather than hardcoding English: this branch re-renders
  // every time App() re-renders (it's a plain `if`, not gated on `screen`), so
  // if the visitor is ALSO signed in on this device/browser (e.g. clicking a
  // verify-email link while still logged in), onAuthChange's async
  // loadUserData() can resolve and call setProfile(...) after the first paint,
  // and this branch picks up the real profile.baseLanguage on that re-render.
  // For a visitor who isn't signed in on this device at all — the common case,
  // since email links are usually opened cold — profile stays null for the
  // whole session and this correctly falls back to English, same as
  // SignInScreen's own hardcoded-English convention for pre-auth screens.
  if (isAuthActionRoute) {
    return (
      <ThemeContext.Provider value={{ darkMode, toggleDark, fontScale, setFontScale }}>
        <I18nProvider baseLanguage={profile?.baseLanguage || "English"}>
          <AuthActionScreen />
        </I18nProvider>
      </ThemeContext.Provider>
    );
  }

  if (screen === "loading") return <LoadingScreen />;

  const baseLang = profile?.baseLanguage || "English";

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDark, fontScale, setFontScale }}>
    <I18nProvider baseLanguage={baseLang}>
      <AppShell
        screen={screen} setScreen={setScreen}
        profile={profile} progress={progress} cards={cards} completedTopics={completedTopics}
        grammarProgress={grammarProgress}
        user={user} isAdminAccount={isAdminAccount} isSimpleViewer={isSimpleViewer} selectedTopic={selectedTopic}
        tokenUsage={tokenUsage} baranAlert={baranAlert}
        pendingDailyPlan={pendingDailyPlan} onDailyPlanAnswered={handleDailyPlanAnswered}
        onResetTokens={resetTokenUsage}
        onOnboardingComplete={handleOnboardingComplete}
        onLessonComplete={handleLessonComplete}
        onCardReview={handleCardReview}
        onLabSessionComplete={handleLabSessionComplete}
        onReviewGrammarPoint={handleReviewGrammarPoint}
        onMarkGrammarPointViewed={handleMarkGrammarPointViewed}
        onSaveGoals={handleSaveGoals}
        onSwitchTargetLanguage={handleSwitchTargetLanguage}
        onReset={handleReset}
        onSignOut={handleSignOut}
        onDeleteAccount={handleDeleteAccount}
        onSetSelectedTopic={setAndPersistTopic}
        onBabiAlertClear={() => setBabiAlert(null)}
        onSetBabiAlert={setBabiAlert}
        subscription={subscription}
        onUpgrade={handleUpgrade}
        onPause={handlePause}
        onResume={handleResume}
        onBuyVoiceMinutes={handleBuyVoiceOverage}
        onChangeVoiceTier={handleChangeVoiceTier}
        onUpdateProfile={handleUpdateProfile}
        teacherTermsAccepted={teacherTermsAccepted}
        onAcceptedTerms={handleAcceptedTerms}
        onPlacementTestExit={handlePlacementTestExit}
        onPlacementTestComplete={handlePlacementTestComplete}
        onStartPlacementTest={() => { setPlacementOrigin("goals"); setScreen("placementTest"); }}
      />
    </I18nProvider>
    </ThemeContext.Provider>
  );
}

function AppShell({
  screen, setScreen, profile, progress, cards, completedTopics, grammarProgress, user, isAdminAccount, isSimpleViewer, selectedTopic,
  tokenUsage, baranAlert, pendingDailyPlan, onDailyPlanAnswered, onResetTokens, onOnboardingComplete, onLessonComplete,
  onCardReview, onLabSessionComplete, onReviewGrammarPoint, onMarkGrammarPointViewed, onSaveGoals, onSwitchTargetLanguage, onReset, onSignOut, onDeleteAccount, onSetSelectedTopic,
  onBabiAlertClear, onSetBabiAlert, subscription, onUpgrade, onPause, onResume, onUpdateProfile,
  onBuyVoiceMinutes, onChangeVoiceTier, teacherTermsAccepted, onAcceptedTerms,
  onPlacementTestExit, onPlacementTestComplete, onStartPlacementTest,
}) {
  const { t, isRTL, lang } = useT();
  const premium = isPremiumActive(profile, subscription, isAdminAccount, isSimpleViewer);
  const frozen = !premium; // trial expired and no paid subscription
  const frozenTopicIds = new Set(profile?.frozenTopicIds || []);
  const showChrome = screen !== "signin" && screen !== "verify-email" && screen !== "onboarding" && screen !== "install-prompt" && screen !== "upgrade";

  // One-shot deep-link target for Grammar Lab, set by GlobalSearch and consumed by
  // GrammarLabView's mount-time initializer (see onInitialPointConsumed there).
  const [grammarDeepLinkId, setGrammarDeepLinkId] = useState(null);

  // Where Teacher Terms' Back button should return to — Home (banner/deep-link) or Teacher
  // Dashboard ("View accepted terms" link). Defaults to "dashboard".
  const [teacherTermsReturnScreen, setTeacherTermsReturnScreen] = useState("dashboard");

  // One-shot deep-link target for Listen & Speak Lab, set by LessonView's "Practice in
  // Listen & Speak Lab" button and consumed by ListenSpeakLab's mount-time initializer (same
  // pattern as grammarDeepLinkId above). Entitlement is NOT re-checked here — navigating to
  // "listenSpeak" already routes through the exact same entitled/upsell/paywall branch every
  // other entry point (the Dashboard card, the More popover) uses, so a non-Elite learner lands
  // on the upgrade screen automatically and this id is simply never consumed.
  const [labDeepLinkTopicId, setLabDeepLinkTopicId] = useState(null);
  const handleGoToListenSpeakForTopic = (topicId) => {
    setLabDeepLinkTopicId(topicId);
    setScreen("listenSpeak");
  };

  // Tracks the flashcards session's reviewed-but-unsaved queue (kept in sync by
  // FlashcardView's onUnsavedChange) so any navigation away from the flashcards screen can be
  // guarded without lifting the whole review session up into App state. A ref (not state)
  // because it only needs to be read at the moment a navigation is attempted.
  const unsavedFlashcardsRef = useRef([]);
  // Companion to unsavedFlashcardsRef: the session's active-minutes-so-far, kept in sync by
  // the same onUnsavedChange callback, so a save triggered from this leave-guard (rather than
  // FlashcardView's own backToOverview/done-button) still credits the time actually spent.
  const unsavedFlashcardsMinutesRef = useRef(0);
  // Screen the user tried to navigate to while flashcards had unsaved progress; non-null
  // shows the leave-confirmation dialog.
  const [flashcardsLeaveTarget, setFlashcardsLeaveTarget] = useState(null);

  // Guarded navigation: use this instead of the raw setScreen for anything that can navigate
  // away from the flashcards screen (bottom nav, header links, search results, etc.) so
  // in-progress reviewed cards are never silently discarded.
  const guardedSetScreen = (target) => {
    if (screen === "flashcards" && target !== "flashcards" && unsavedFlashcardsRef.current.length > 0) {
      setFlashcardsLeaveTarget(target);
      return;
    }
    setScreen(target);
  };
  const handleFlashcardsLeaveSave = () => {
    onCardReview(unsavedFlashcardsRef.current, { toDashboard: false, minutes: unsavedFlashcardsMinutesRef.current });
    unsavedFlashcardsRef.current = [];
    unsavedFlashcardsMinutesRef.current = 0;
    const target = flashcardsLeaveTarget;
    setFlashcardsLeaveTarget(null);
    setScreen(target);
  };
  const handleFlashcardsLeaveDiscard = () => {
    unsavedFlashcardsRef.current = [];
    unsavedFlashcardsMinutesRef.current = 0;
    const target = flashcardsLeaveTarget;
    setFlashcardsLeaveTarget(null);
    setScreen(target);
  };
  const handleFlashcardsLeaveCancel = () => setFlashcardsLeaveTarget(null);

  // Same paywall check AllLessonsView.goToLesson already uses — locked/paywalled results stay
  // visible in search (per product decision), gated only on click, same as All Lessons today.
  const handleSearchSelectTopic = (topic) => {
    if (frozen && !frozenTopicIds.has(topic.topic_id)) { onUpgrade?.(); return; }
    onSetSelectedTopic(topic);
    guardedSetScreen("lesson");
  };

  // Word deep-link = Option A (Current Lesson position): write lastLessonPosition before
  // navigating, then let LessonView's existing mount-time logic resolve it. Known accepted
  // edge case: if the topic is partially completed and this word is already learned (not in
  // the active newWords track), LessonView falls back to word 0 rather than the exact word.
  //
  // LessonView resolves lastLessonPosition only in a useState lazy initializer (mount-time).
  // Selecting a search result while already on the "lesson" screen changes selectedTopic's
  // props but does NOT unmount/remount LessonView, so that initializer never reruns and the
  // stale currentIndex from the previous word sticks — which is exactly why the deep-link only
  // worked from Home (a dashboard->lesson screen change always mounts fresh). wordNavSeq is
  // bumped on every search-word selection and folded into LessonView's `key` below to force a
  // remount, even when the new word is in the SAME topic as the one currently open.
  const [wordNavSeq, setWordNavSeq] = useState(0);
  const handleSearchSelectWord = (word, topic) => {
    if (frozen && !frozenTopicIds.has(topic.topic_id)) { onUpgrade?.(); return; }
    save(LESSON_POSITION_KEY, { topicId: topic.topic_id, wordId: word.word_id });
    onSetSelectedTopic(topic);
    setWordNavSeq(s => s + 1);
    guardedSetScreen("lesson");
  };

  const handleSearchSelectGrammarPoint = (grammarPointId) => {
    setGrammarDeepLinkId(grammarPointId);
    guardedSetScreen("grammarLab");
  };

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div className={`app${showChrome ? " app--with-nav" : ""}`} dir={isRTL ? "rtl" : "ltr"} data-lang={lang.toLowerCase()}>
        {screen === "signin"          && <SignInScreen />}
        {screen === "verify-email"    && <VerifyEmailScreen />}
        {screen === "install-prompt"  && <InstallPromptScreen onDone={() => setScreen("dashboard")} />}
        {showChrome && (
          <>
            <Header
              screen={screen} setScreen={guardedSetScreen} cards={cards} profile={profile} subscription={subscription}
              onReset={onReset} onSignOut={onSignOut} onUpgrade={onUpgrade}
              premium={premium} frozenTopicIds={frozenTopicIds}
              onSearchSelectTopic={handleSearchSelectTopic}
              onSearchSelectWord={handleSearchSelectWord}
              onSearchSelectGrammarPoint={handleSearchSelectGrammarPoint}
            />
            <TrialExpiredBar profile={profile} subscription={subscription} premium={premium} onUpgrade={onUpgrade} />
          </>
        )}
        {screen === "onboarding"  && <OnboardingScreen onComplete={onOnboardingComplete} googleName={user?.displayName} destLang="Turkish" />}
        {screen === "dashboard"   && <Dashboard profile={profile} progress={progress} cards={cards} setScreen={setScreen} selectedTopic={selectedTopic} tokenUsage={tokenUsage} onResetTokens={onResetTokens} subscription={subscription} premium={premium} onUpgrade={onUpgrade} teacherTermsAccepted={teacherTermsAccepted} onGoToTeacherTerms={() => { setTeacherTermsReturnScreen("dashboard"); setScreen("teacherTerms"); }} />}
        {screen === "lesson"      && (frozen && !frozenTopicIds.has(selectedTopic?.topic_id)
          ? <PaywallScreen profile={profile} subscription={subscription} onUpgrade={onUpgrade} onGoToSettings={() => setScreen("settings")} onBack={() => setScreen("dashboard")} />
          : <LessonView key={`${selectedTopic?.topic_id}-${wordNavSeq}`} profile={profile} cards={cards} topic={selectedTopic} completedTopics={completedTopics} onComplete={onLessonComplete} onBack={() => setScreen("dashboard")} destLang={(profile?.targetLanguage || "Turkish").toLowerCase()} onGoToListenSpeak={handleGoToListenSpeakForTopic} />
        )}
        {screen === "allLessons"  && <AllLessonsView setScreen={setScreen} setSelectedTopic={onSetSelectedTopic} progress={progress} cards={cards} completedTopics={completedTopics} onLimitReached={() => onSetBabiAlert(t("chat.lessonLimit", { limit: DAILY_LESSON_LIMIT }))} premium={premium} frozenTopicIds={frozenTopicIds} onUpgrade={onUpgrade} profile={profile} />}
        {screen === "flashcards"  && <FlashcardView cards={cards} profile={profile} onComplete={onCardReview} onBack={() => guardedSetScreen("dashboard")} destLang={profile?.targetLanguage || "Turkish"} onUnsavedChange={(rev, mins) => { unsavedFlashcardsRef.current = rev; unsavedFlashcardsMinutesRef.current = mins || 0; }} />}
        {screen === "upgrade"     && <PaywallScreen profile={profile} subscription={subscription} onUpgrade={onUpgrade} onGoToSettings={() => setScreen("settings")} onBack={() => setScreen("dashboard")} />}
        {screen === "goals"       && <GoalsView key={profile?.targetLanguage || "Turkish"} profile={profile} onSave={onSaveGoals} onSwitchTargetLanguage={onSwitchTargetLanguage} onBack={() => setScreen("dashboard")} onTakePlacementTest={onStartPlacementTest} />}
        {screen === "progress"    && <ProgressView progress={progress} profile={profile} cards={cards} onBack={() => setScreen("dashboard")} />}
        {screen === "myVocabulary" && <MyVocabularyView cards={cards} completedTopics={completedTopics} profile={profile} onBack={() => setScreen("dashboard")} />}
        {screen === "listenSpeak" && (
          hasVoiceLabAccess(profile, subscription, isAdminAccount, isSimpleViewer)
            ? <ListenSpeakLab profile={profile} subscription={subscription} isAdminAccount={isAdminAccount} isSimpleViewer={isSimpleViewer} completedTopics={completedTopics} onUpdateProfile={onUpdateProfile} onSessionComplete={onLabSessionComplete} onBack={() => setScreen("dashboard")} initialTopicId={labDeepLinkTopicId} onInitialTopicConsumed={() => setLabDeepLinkTopicId(null)} />
            : subscription?.status === "active"
              ? <LabEliteUpsell onGoToSettings={() => setScreen("settings")} onBack={() => setScreen("dashboard")} />
              : <PaywallScreen profile={profile} subscription={subscription} onUpgrade={onUpgrade} onGoToSettings={() => setScreen("settings")} onBack={() => setScreen("dashboard")} />
        )}
        {screen === "settings"    && <SettingsView user={user} profile={profile} progress={progress} cards={cards} subscription={subscription} isAdminAccount={isAdminAccount} isSimpleViewer={isSimpleViewer} onBack={() => setScreen("dashboard")} onReset={onReset} onDeleteAccount={onDeleteAccount} onUpgrade={onUpgrade} onPause={onPause} onResume={onResume} onChangeVoiceTier={onChangeVoiceTier} onUpdateProfile={onUpdateProfile} />}
        {screen === "grammarLab"  && <GrammarLabView profile={profile} grammarProgress={grammarProgress} onReview={onReviewGrammarPoint} onMarkViewed={onMarkGrammarPointViewed} onBack={() => setScreen("dashboard")} initialPointId={grammarDeepLinkId} onInitialPointConsumed={() => setGrammarDeepLinkId(null)} />}
        {/* /teacher-dashboard. Role-guarded IN the component (not just here): the Header/Home
            links are only hidden for a learner, so a directly-typed URL or a stale bookmark
            still reaches this branch — TeacherDashboard re-checks isTeacher and bounces anyone
            else back to the dashboard. */}
        {screen === "teacherDashboard" && <TeacherDashboard profile={profile} user={user} onBack={() => setScreen("dashboard")} onUpdateProfile={onUpdateProfile} teacherTermsAccepted={teacherTermsAccepted} onGoToTerms={() => { setTeacherTermsReturnScreen("teacherDashboard"); setScreen("teacherTerms"); }} />}
        {/* /teacher-terms — the in-app Teacher Partner Terms + the ONLY acceptance surface.
            Same role guard as TeacherDashboard. The public speafy.com/affiliate/teacher-terms
            page is read-only and links here. Back returns to wherever this screen was reached
            from (Home banner/deep-link, or the Teacher Dashboard link). */}
        {screen === "teacherTerms" && <TeacherTermsView profile={profile} alreadyAccepted={teacherTermsAccepted} onAccepted={onAcceptedTerms} onBack={() => setScreen(teacherTermsReturnScreen)} />}
        {screen === "placementTest" && <PlacementTestView profile={profile} onExit={onPlacementTestExit} onComplete={onPlacementTestComplete} />}
        {showChrome && <BottomNav screen={screen} setScreen={guardedSetScreen} cards={cards} grammarProgress={grammarProgress} profile={profile} />}
        {showChrome && <AgentChat profile={profile} progress={progress} cards={cards} grammarProgress={grammarProgress} completedTopics={completedTopics} selectedTopic={selectedTopic} screen={screen} baranAlert={baranAlert} onBabiAlertClear={onBabiAlertClear} pendingDailyPlan={pendingDailyPlan} onDailyPlanAnswered={onDailyPlanAnswered} onUpdateProfile={onUpdateProfile} subscription={subscription} isAdminAccount={isAdminAccount} isSimpleViewer={isSimpleViewer} onBuyVoiceMinutes={onBuyVoiceMinutes} />}
        {flashcardsLeaveTarget && (
          <FlashcardsLeaveConfirmDialog
            onSave={handleFlashcardsLeaveSave}
            onDiscard={handleFlashcardsLeaveDiscard}
            onCancel={handleFlashcardsLeaveCancel}
          />
        )}
      </div>
    </>
  );
}

function FlashcardsLeaveConfirmDialog({ onSave, onDiscard, onCancel }) {
  const { t } = useT();
  return (
    <div className="fc-modal-overlay" onClick={onCancel}>
      <div className="fc-modal" onClick={e => e.stopPropagation()}>
        <div className="fc-modal-title">{t("flashcards.leaveConfirmTitle")}</div>
        <p className="fc-modal-body">{t("flashcards.leaveConfirmBody")}</p>
        <button className="primary-btn" onClick={onSave}>{t("flashcards.leaveConfirmSave")}</button>
        <div style={{display:"flex",gap:8,marginTop:10}}>
          <button className="settings-cancel-btn" style={{flex:1}} onClick={onCancel}>{t("flashcards.leaveConfirmCancel")}</button>
          <button className="settings-delete-final-btn" style={{flex:1,margin:0}} onClick={onDiscard}>{t("flashcards.leaveConfirmDiscard")}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// LOADING
// ═══════════════════════════════════════════════════════════════
function LoadingScreen() {
  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div className="app" style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh"}}>
        <div style={{textAlign:"center"}}>
          <img src="/speafy-square-logo-192x192.png" alt="Speafy" style={{width:72,height:72,marginBottom:20,borderRadius:16}} />
          <div className="spinner" />
          <p style={{marginTop:16,color:"#8B7355",fontFamily:"Nunito, sans-serif"}}>Loading your journey…</p>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// INSTALL PROMPT SCREEN
// ═══════════════════════════════════════════════════════════════
function InstallPromptScreen({ onDone }) {
  const { darkMode } = useTheme();
  const dk = darkMode;
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const dismiss = () => { save("installPromptShown", true); onDone(); };

  const steps = isIOS ? [
    <><strong>⬆ Share</strong> button at the bottom of Safari</>,
    <><strong>"Add to Home Screen"</strong></>,
    <><strong>"Add"</strong> ✓</>,
  ] : [
    <><strong>⋮ menu</strong> in Chrome's top-right corner</>,
    <><strong>"Add to Home screen"</strong></>,
    <><strong>"Add"</strong> ✓</>,
  ];

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div style={{
        minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center",
        justifyContent:"center", padding:"32px 20px",
        background: dk ? "#12080A" : "linear-gradient(160deg,#FDF6EE 0%,#F5E9D8 100%)",
        fontFamily:"'Nunito',sans-serif",
      }}>
        {/* Babi avatar */}
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:20}}>
          <div style={{width:72,height:72,borderRadius:"50%",overflow:"hidden",marginBottom:8,boxShadow:"0 4px 24px rgba(123,30,30,0.18)"}}>
            <img src="/babi%20fantasy2.jpg?v=2" alt="Babi" style={{width:"100%",height:"100%",objectFit:"cover"}} />
          </div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1rem",fontWeight:700,color:dk?"#F0E0D0":"#2C1810"}}>Babi</div>
          <div style={{fontSize:"0.75rem",color:dk?"#9A7060":"#8B7355",marginTop:2}}>Your AI tutor</div>
        </div>

        {/* Card */}
        <div style={{
          width:"100%", maxWidth:400,
          background:dk?"#1F1212":"#fff",
          border:`1px solid ${dk?"#3A2020":"#F0DFC0"}`,
          borderRadius:22, padding:"28px 28px 24px",
          boxShadow:"0 8px 40px rgba(123,30,30,0.10)",
        }}>
          <p style={{fontSize:"0.9375rem",color:dk?"#F0E0D0":"#2C1810",lineHeight:1.7,marginBottom:20}}>
            Quick tip before you begin! 😊<br/><br/>
            {isIOS
              ? "Add Speafy to your home screen for the best experience — it opens instantly, no browser bar, just like a real app."
              : "Install Speafy on your home screen for instant one-tap access — just like a native app."
            }
          </p>

          <ol style={{paddingLeft:20,margin:"0 0 20px",display:"flex",flexDirection:"column",gap:10}}>
            {steps.map((step, i) => (
              <li key={i} style={{fontSize:"0.875rem",color:dk?"#D0B0A0":"#5C3D2E",lineHeight:1.6}}>
                Tap the {step}
              </li>
            ))}
          </ol>

          <p style={{fontSize:"0.8125rem",color:dk?"#9A7060":"#8B7355",marginBottom:20}}>
            It only takes 10 seconds!
          </p>

          <button
            onClick={dismiss}
            style={{
              width:"100%", padding:"12px", marginBottom:10,
              background:"linear-gradient(135deg,#7B1E1E,#A33030)", color:"#fff",
              border:"none", borderRadius:12, cursor:"pointer",
              fontFamily:"'Nunito',sans-serif", fontSize:"0.9375rem", fontWeight:700,
            }}>
            Got it!
          </button>
          <button
            onClick={dismiss}
            style={{
              width:"100%", padding:"12px",
              background:"transparent", color:dk?"#9A7060":"#8B7355",
              border:`1px solid ${dk?"#3A2020":"#E8D5B7"}`, borderRadius:12, cursor:"pointer",
              fontFamily:"'Nunito',sans-serif", fontSize:"0.9375rem", fontWeight:600,
            }}>
            Maybe later
          </button>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// HEADER
// ═══════════════════════════════════════════════════════════════
function Header({
  screen, setScreen, cards, profile, subscription, onReset, onSignOut, onUpgrade,
  premium, frozenTopicIds, onSearchSelectTopic, onSearchSelectWord, onSearchSelectGrammarPoint,
}) {
  const { t } = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const baseLanguage = profile?.baseLanguage || "English";
  const deeplyRetained = cards.filter(c => (c.interval || 0) >= 90).length;
  const badge = getBadge(deeplyRetained);
  const nav = [
    { key: "dashboard", label: t("header.home") },
    { key: "goals",     label: t("header.goals") },
    // Teacher-partner only. Conditionally SPLICED in (not rendered-then-hidden) so it never
    // exists in the DOM for a learner. Same conditional-visibility approach as the Pro/trial
    // sub-badge above — and, like that badge, gated purely on role, never on subscription/trial.
    ...(isTeacher(profile) ? [{ key: "teacherDashboard", label: (
      <><img className="nav-btn-ic" src="/teacher-icon.png" alt="" aria-hidden="true" />{t("teacherDash.link")}</>
    ) }] : []),
    { key: "settings",  label: `⚙ ${t("settings.title")}` },
  ];
  const go = (key) => { setScreen(key); setMenuOpen(false); };
  return (
    <header className="header">
      <div className="header-brand">
        <span style={{fontSize:"1.625rem"}}>{badge.icon}</span>
        <div>
          <div className="brand-name">{profile?.name || t("header.learner")}</div>
          <div className="brand-sub" style={{display:"flex",alignItems:"center",gap:6}}>
            {t(`badges.${badge.key}`)}
            {subscription?.status === "active"
              ? (getVoiceTier(subscription) === VOICE_TIER_ELITE
                  ? <span className="sub-badge sub-badge--elite">{t("trial.eliteBadge")}</span>
                  : <span className="sub-badge sub-badge--pro">{t("trial.proBadge")}</span>)
              : subscription?.status === "paused"
                ? <button className="sub-badge sub-badge--paused" onClick={() => setScreen("settings")}>{t("trial.pausedBadge")}</button>
                : !premium && profile?.trialStartDate && trialDaysLeft(profile) > 0
                  ? <button className="sub-badge sub-badge--trial" onClick={() => onUpgrade()}>{t("trial.daysLeftBadge", { n: trialDaysLeft(profile) })}</button>
                  : null
            }
          </div>
        </div>
      </div>
      <GlobalSearch
        premium={premium}
        frozenTopicIds={frozenTopicIds}
        onUpgrade={onUpgrade}
        onSelectTopic={onSearchSelectTopic}
        onSelectWord={onSearchSelectWord}
        onSelectGrammarPoint={onSearchSelectGrammarPoint}
        baseLanguage={baseLanguage}
        targetLanguage={profile?.targetLanguage}
      />
      <button className="hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
        {menuOpen ? "✕" : "☰"}
      </button>
      <nav className={`header-nav${menuOpen ? " open" : ""}`}>
        {nav.map(n => (
          <button key={n.key} className={`nav-btn${screen===n.key?" active":""}`} onClick={() => go(n.key)}>
            {n.label}
          </button>
        ))}
        <button className="nav-btn" onClick={() => { onSignOut(); setMenuOpen(false); }} style={{opacity:0.8}}>{t("header.signOut")}</button>
      </nav>
    </header>
  );
}

// Trial-expired notice, deliberately a full-width strip below the header rather than a badge
// inside it — the badge's longer "Trial ended" text was wrapping/crowding the header's other
// flex items on narrow screens. Living on its own row sidesteps that and reads as more
// prominent, which is the point: this is the one state where the user genuinely has no access left.
function TrialExpiredBar({ profile, subscription, premium, onUpgrade }) {
  const { t } = useT();
  if (premium) return null;
  if (subscription?.status === "active" || subscription?.status === "paused") return null;
  if (!profile?.trialStartDate || trialDaysLeft(profile) > 0) return null;
  return (
    <button className="trial-expired-bar" onClick={() => onUpgrade?.()}>
      {t("trial.expiredBadge")}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// BOTTOM NAV
// ═══════════════════════════════════════════════════════════════
function BottomNav({ screen, setScreen, cards, grammarProgress, profile }) {
  const { t, lang } = useT();
  const _today = todayISO();
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();
  const dueCount = cards.filter(c =>
    (!c.d1Mastered && c.due <= _today) || (c.d2 && !c.d2Mastered && c.d2.due <= _today)
  ).length;
  // Independent of the flashcard due count above — never feeds the overwhelm gate.
  // Not-reviewed count (total minus viewed), not the spaced-repetition due count —
  // countGrammarDue stays near the full total since never-reviewed points are always "due".
  const grammarDueCount = countGrammarNotReviewed(langCode, grammarProgress);

  const [moreOpen, setMoreOpen] = useState(false);
  const moreTriggerRef = useRef(null);
  const moreMenuRef = useRef(null);

  const stripIcon = (s) => s.replace(/^\S+\s+/, "");

  const isActive = (key) => {
    if (key === "more") return screen === "dashboard" || screen === "progress" || screen === "myVocabulary" || screen === "listenSpeak";
    return screen === key;
  };

  const tabs = [
    { key: "grammarLab",  icon: "📘", label: stripIcon(t("header.grammarLab")), badge: grammarDueCount > 0 ? formatNumber(grammarDueCount > 99 ? "99+" : grammarDueCount, lang) : null, badgeVariant: "grammar" },
    { key: "allLessons",  icon: "📚", label: stripIcon(t("header.allLessons")) },
    { key: "lesson",      icon: "📖", label: stripIcon(t("header.currentLesson")) },
    { key: "flashcards",  icon: "🃏", label: stripIcon(t("header.flashcards")), badge: dueCount > 0 ? formatNumber(dueCount > 99 ? "99+" : dueCount, lang) : null },
  ];

  // Home is anchored first and stays first — any future entry goes below Home, never above it.
  // "Listen & Speak" is always listed (Elite-tier feature); a non-qualifying tap is routed to the
  // upgrade screen by AppShell, not hidden or disabled here.
  const moreItems = [
    { key: "dashboard",     label: stripIcon(t("header.home")) },
    { key: "progress",      label: stripIcon(t("header.progress")) },
    { key: "myVocabulary",  label: stripIcon(t("header.myVocabulary")) },
    { key: "listenSpeak",   label: stripIcon(t("header.listenSpeak")) },
  ];

  const closeMore = () => {
    setMoreOpen(false);
    moreTriggerRef.current?.focus();
  };

  useEffect(() => {
    if (!moreOpen) return;
    const items = () => Array.from(moreMenuRef.current?.querySelectorAll('[role="menuitem"]') || []);
    items()[0]?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") { e.preventDefault(); closeMore(); return; }
      if (e.key === "Tab") {
        const focusables = items();
        if (focusables.length === 0) return;
        const first = focusables[0], last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    const handleClickOutside = (e) => {
      if (!moreMenuRef.current?.contains(e.target) && !moreTriggerRef.current?.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moreOpen]);

  const handleTriggerKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setMoreOpen(o => !o);
    }
  };

  return (
    <nav className={`bottom-nav${moreOpen ? " menu-open" : ""}`}>
      <div className="bottom-nav-inner">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`bottom-nav-tab${isActive(tab.key) ? " active" : ""}`}
            onClick={() => setScreen(tab.key)}
          >
            <span className="bottom-nav-icon">
              {tab.icon}
              {tab.badge && <span className={`bottom-nav-badge${tab.badgeVariant ? ` bottom-nav-badge--${tab.badgeVariant}` : ""}`}>{tab.badge}</span>}
            </span>
            <span className="bottom-nav-label">{tab.label}</span>
          </button>
        ))}

        <div className="bottom-nav-more-wrap">
          <button
            ref={moreTriggerRef}
            className={`bottom-nav-tab${isActive("more") ? " active" : ""}`}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            aria-label={stripIcon(t("header.more"))}
            onClick={() => setMoreOpen(o => !o)}
            onKeyDown={handleTriggerKeyDown}
          >
            <span className="bottom-nav-icon">⋯</span>
            <span className="bottom-nav-label">{stripIcon(t("header.more"))}</span>
          </button>

          {moreOpen && (
            <div className="bottom-nav-more-menu" role="menu" ref={moreMenuRef} aria-label={stripIcon(t("header.more"))}>
              {moreItems.map(item => (
                <button
                  key={item.key}
                  role="menuitem"
                  className="bottom-nav-more-item"
                  aria-label={item.label}
                  onClick={() => { setScreen(item.key); closeMore(); }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

// ═══════════════════════════════════════════════════════════════
// ONBOARDING
// ═══════════════════════════════════════════════════════════════
function OnboardingScreen({ onComplete, googleName, destLang = "Turkish" }) {
  const [consented, setConsented] = useState(false);
  const [agreedAge, setAgreedAge] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedMarketing, setAgreedMarketing] = useState(false);
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [lang, setLang] = useState("");
  const [country, setCountry] = useState("");
  const [showCustomLang, setShowCustomLang] = useState(false);
  const [customLang, setCustomLang] = useState("");

  const canNext = [
    name.trim().length > 0,
    lang.length > 0,
    country.length > 0,
  ];

  const handleNext = () => setStep(s => s + 1);

  const confirmCustomLang = () => {
    const val = customLang.trim();
    if (!val || val.toLowerCase() === destLang.toLowerCase()) return;
    setLang(val);
    setShowCustomLang(false);
    setCustomLang("");
  };

  const handleStart = () => {
    onComplete({
      name: name.trim(),
      baseLanguage: lang,
      country,
      targetWords: 3000,
      courseLengthDays: 180,
      wordsPerDay: 3000 / 180,
      startDate: todayISO(),
    }, { consentMarketing: agreedMarketing });
  };

  if (!consented) return (
    <div className="onboarding">
      <div className="onboarding-card">
        <div className="avatar-row">
          <div className="agent-avatar"><img src="/babi%20fantasy2.jpg?v=2" alt="Babi" style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%"}} /></div>
          <div className="agent-id"><strong>Babi</strong> · Your Turkish Tutor</div>
        </div>
        <div className="bubble"><p>Welcome! Before we start, please confirm a few things.</p></div>
        <div style={{display:"flex",flexDirection:"column",gap:12,margin:"16px 0"}}>
          <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
            <input type="checkbox" checked={agreedAge} onChange={e=>setAgreedAge(e.target.checked)}
              style={{marginTop:3,width:16,height:16,flexShrink:0,accentColor:"#7B1E1E",cursor:"pointer"}} />
            <span style={{fontSize:"0.75rem",color:"#5C3D2E",lineHeight:1.7}}>
              I confirm that I am at least 13 years old and agree to the Terms of Service and Privacy Policy. <span style={{color:"#7B1E1E",fontWeight:700}}>*</span>
            </span>
          </label>
          <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
            <input type="checkbox" checked={agreedTerms} onChange={e=>setAgreedTerms(e.target.checked)}
              style={{marginTop:3,width:16,height:16,flexShrink:0,accentColor:"#7B1E1E",cursor:"pointer"}} />
            <span style={{fontSize:"0.75rem",color:"#5C3D2E",lineHeight:1.7}}>
              I agree to the{" "}
              <a href="https://speafy.com/terms" target="_blank" rel="noopener noreferrer" style={{color:"#7B1E1E",textDecoration:"underline"}}>Terms of Service</a>
              {" "}and{" "}
              <a href="https://speafy.com/privacy" target="_blank" rel="noopener noreferrer" style={{color:"#7B1E1E",textDecoration:"underline"}}>Privacy Policy</a>. <span style={{color:"#7B1E1E",fontWeight:700}}>*</span>
            </span>
          </label>
          <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
            <input type="checkbox" checked={agreedMarketing} onChange={e=>setAgreedMarketing(e.target.checked)}
              style={{marginTop:3,width:16,height:16,flexShrink:0,accentColor:"#7B1E1E",cursor:"pointer"}} />
            <span style={{fontSize:"0.75rem",color:"#5C3D2E",lineHeight:1.7}}>
              Send me updates, notifications, and special offers. <span style={{color:"#B0956A"}}>(Optional)</span>
            </span>
          </label>
        </div>
        <p style={{fontSize:"0.6875rem",color:"#8B7355",marginBottom:14,lineHeight:1.5}}>
          <span style={{color:"#7B1E1E",fontWeight:700}}>*</span> Required to continue
        </p>
        <button className="primary-btn" disabled={!agreedAge || !agreedTerms} onClick={() => setConsented(true)}>
          Continue →
        </button>
      </div>
    </div>
  );

  // Nested provider (independent of the outer App-level one, which still reads
  // profile?.baseLanguage — null until onComplete fires) so the country step and
  // summary screen switch language live as soon as `lang` is picked, instead of
  // waiting for a saved profile.
  return (
    <I18nProvider baseLanguage={lang || "English"}>
      <OnboardingBody
        step={step} name={name} setName={setName} lang={lang} setLang={setLang}
        country={country} setCountry={setCountry}
        showCustomLang={showCustomLang} setShowCustomLang={setShowCustomLang}
        customLang={customLang} setCustomLang={setCustomLang}
        canNext={canNext} handleNext={handleNext} confirmCustomLang={confirmCustomLang} handleStart={handleStart}
        destLang={destLang}
      />
    </I18nProvider>
  );
}

function OnboardingBody({
  step, name, setName, lang, setLang, country, setCountry,
  showCustomLang, setShowCustomLang, customLang, setCustomLang,
  canNext, handleNext, confirmCustomLang, handleStart, destLang,
}) {
  const { t, isRTL, langName } = useT();
  const [typing, setTyping] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (step >= 3) return;
    const msgs = [
      STEPS[0].q,
      STEPS[1].q(name || "friend"),
      t("onboarding.countryQuestion"),
    ];
    setTyping(true);
    setMessage("");
    const full = msgs[step];
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setMessage(full.slice(0, i));
      if (i >= full.length) { clearInterval(iv); setTyping(false); }
    }, 14);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Steps 0-1 (name + language picker) always show hardcoded English/Latin text (see the
  // STEPS comment above) regardless of which base language gets picked — so `dir` must stay
  // "ltr" through step 1 even after the learner taps an RTL language, otherwise that still-visible
  // English bubble/button text flips to right-aligned before Babi has actually said anything in
  // the new language. From step 2 (country) onward the content really is in the picked language,
  // so `isRTL` applies from there.
  return (
    <div className="onboarding" dir={step >= 2 && isRTL ? "rtl" : "ltr"}>
      <div className="onboarding-card">
        <div className="step-dots">
          {[0, 1, 2].map(i => (
            <div key={i} className={`sdot${i < step ? " done" : i === step ? " active" : ""}`} />
          ))}
        </div>

        <div className="avatar-row">
          <div className="agent-avatar"><img src="/babi%20fantasy2.jpg?v=2" alt="Babi" style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%"}} /></div>
          <div className="agent-id"><strong>Babi</strong> · Your Turkish Tutor</div>
        </div>

        {step < 3 && (
          <div className="bubble">
            {message.split("\n").map((l, i) => <p key={i} style={{marginBottom: l ? 6 : 0}}>{l}</p>)}
            {typing && <span className="cursor">▋</span>}
          </div>
        )}

        {/* Step 0: Name */}
        {step === 0 && !typing && (
          <div className="input-group">
            <input className="text-input" placeholder="Type your name…" value={name}
              onChange={e => setName(e.target.value)} onKeyDown={e => e.key==="Enter" && canNext[0] && handleNext()} autoFocus />
            <button className="primary-btn cta-btn" disabled={!canNext[0]} onClick={handleNext}><span>Continue</span><span className="cta-arrow" aria-hidden="true">→</span></button>
          </div>
        )}

        {/* Step 1: Language */}
        {step === 1 && !typing && (
          <div className="input-group">
            <div className="lang-grid">
              {BASE_LANGS.map(l => {
                const isTarget = l.toLowerCase() === destLang.toLowerCase();
                return (
                  <button key={l} className={`lang-btn${lang===l?" sel":""}${isTarget?" tgt-disabled":""}`}
                    onClick={() => { if (!isTarget) { setLang(l); setShowCustomLang(false); setCustomLang(""); } }}
                    disabled={isTarget}>{l}</button>
                );
              })}
            </div>
            {/* Disabled 2026-07-25 — see the matching Goals-page picker (App.js Dashboard/GoalsView)
                for why. Re-enable (drop `disabled`, restore the onClick) once more languages are
                added — tracked in speafy_dev_task_backlog.md. */}
            <button
              className={`lang-btn${lang && !BASE_LANGS.filter(l => l.toLowerCase() !== destLang.toLowerCase()).includes(lang) ? " sel" : ""}`}
              style={{width:"100%",marginTop:8,textAlign:"center",opacity:0.5,cursor:"not-allowed"}}
              disabled
            >
              {lang && !BASE_LANGS.filter(l => l.toLowerCase() !== destLang.toLowerCase()).includes(lang)
                ? `✓ ${lang}` : "Other languages (coming soon)"}
            </button>
            {showCustomLang && (
              <div style={{display:"flex",gap:8,alignItems:"stretch"}}>
                <select
                  value={customLang}
                  onChange={e => setCustomLang(e.target.value)}
                  style={{flex:1,minWidth:0,padding:"9px 8px",border:"2px solid #E8D5B7",borderRadius:10,fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:600,color:"#5C3D2E",background:"#fff",cursor:"pointer",outline:"none"}}
                >
                  <option value="">Select a language…</option>
                  {EXTRA_LANGS.filter(l => l.toLowerCase() !== destLang.toLowerCase()).map(l => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
                <button onClick={confirmCustomLang}
                  style={{flexShrink:0,padding:"9px 4px",width:"calc(25% - 6px)",border:"2px solid #7B1E1E",borderRadius:10,background:"#7B1E1E",color:"#fff",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:600,cursor:"pointer"}}>OK</button>
              </div>
            )}
            <button className="primary-btn cta-btn" disabled={!canNext[1]} onClick={handleNext}><span>Continue</span><span className="cta-arrow" aria-hidden="true">→</span></button>
          </div>
        )}

        {/* Step 2: Country — its own turn, separate from language (previously bundled into the
            same question/screen as language, where it was easy to miss below the language
            grid and the "Other language…" button). */}
        {step === 2 && !typing && (
          <div className="input-group">
            <select
              value={country}
              onChange={e => setCountry(e.target.value)}
              style={{width:"100%",padding:"9px 8px",border:"2px solid #E8D5B7",borderRadius:10,fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:600,color:"#5C3D2E",background:"#fff",cursor:"pointer",outline:"none"}}
              autoFocus
            >
              <option value="">{t("onboarding.countryPlaceholder")}</option>
              {COUNTRIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button className="primary-btn cta-btn" disabled={!canNext[2]} onClick={handleNext}><span>{t("onboarding.letsGo")}</span><span className="cta-arrow" aria-hidden="true">→</span></button>
          </div>
        )}

        {/* Step 3: Summary */}
        {step === 3 && (
          <div className="summary-card">
            <p style={{fontSize:"0.9375rem",marginBottom:20,lineHeight:1.6,color:"#2C1810"}}>
              {isolateInterpolation(t("onboarding.summaryIntro"), "name", name)}
            </p>
            <div className="summary-grid" style={{gridTemplateColumns:"1fr 1fr"}}>
              <div className="sum-item">
                <div className="sum-num" style={{fontSize:"1.25rem"}}>{name}</div>
                <div className="sum-lbl">{t("onboarding.yourNameLabel")}</div>
              </div>
              <div className="sum-item accent">
                <div className="sum-num" style={{fontSize:"1.25rem"}}>{lang === "Other" ? (customLang || "Other") : lang}</div>
                <div className="sum-lbl">{t("onboarding.baseLanguageLabel")}</div>
              </div>
            </div>
            <p style={{fontSize:"0.8125rem",color:"#8B7355",margin:"16px 0",lineHeight:1.6}}>
              {t("onboarding.goalsHint")}
            </p>
            <button className="primary-btn" onClick={handleStart}>{t("onboarding.startLearning", { destLang: langName(destLang) })}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════
function Dashboard({ profile, progress, cards, setScreen, selectedTopic, tokenUsage, onResetTokens, subscription, premium, onUpgrade, teacherTermsAccepted, onGoToTeacherTerms }) {
  const { t, lang, langName } = useT();
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();

  const handleShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: "Speafy",
        text: `🔥 ${progress.streak}-day streak! I've learned ${progress.wordsLearned} words so far on Speafy.`,
        url: "https://speafy.com",
      });
    } catch { /* user cancelled or share failed — silently ignore */ }
  };
  const tips = t("dashboard.tips");
  const [tip] = useState(() => {
    const arr = Array.isArray(tips) ? tips : [];
    return arr[Math.floor(Math.random() * (arr.length || 1))] || "";
  });
  if (!profile || !progress) return null;
  const _today = todayISO();
  const dueCards = cards.filter(c =>
    (!c.d1Mastered && c.due <= _today) || (c.d2 && !c.d2Mastered && c.d2.due <= _today)
  ).length;
  const elapsed = daysSince(profile.startDate);
  const expected = Math.round(profile.wordsPerDay * elapsed);
  const actual = progress.wordsLearned;
  const delta = actual - expected;
  const pctGoal = Math.min(100, Math.round((actual / profile.targetWords) * 100));
  const pctExpected = Math.min(100, Math.round((expected / profile.targetWords) * 100));
  const status = delta >= 5 ? "ahead" : delta <= -5 ? "behind" : "on-track";
  const statusLabel = {
    ahead: t("dashboard.statusAhead", { delta }),
    behind: t("dashboard.statusBehind", { delta: Math.abs(delta) }),
    "on-track": t("dashboard.statusOnTrack"),
  };
  const todayLog = progress.dailyLog.find(l => l.date === todayISO());
  const todayWords = todayLog?.words || 0;

  return (
    <div className="dashboard">
      <div style={{textAlign:"center",paddingBottom:16}}>
        <img src="/Speafy-logo-bg-removed-300x100.png" alt="Speafy" style={{height:60}} />
      </div>
      {/* Welcome Banner */}
      <div className="welcome-banner">
        <div>
          <div className="welcome-name">{isolateInterpolation(t("dashboard.greeting"), "name", profile.name)}</div>
          <div className="welcome-sub">
            {progress.streak > 0
              ? t("dashboard.streakMsg", { streak: progress.streak })
              : t("dashboard.noStreak")}
          </div>
          {(progress.protectionDays ?? getProtectionDays(progress.streak)) > 0 && (
            <div className="welcome-sub" style={{marginTop:2}}>
              {t("dashboard.streakProtectionDays", { n: formatNumber(progress.protectionDays ?? getProtectionDays(progress.streak), lang) })}
            </div>
          )}
          <div style={{marginTop:12,fontSize:"0.8125rem",opacity:0.85}} dir="auto">
            {t("dashboard.dayInfo", { lang: langName(profile.baseLanguage), day: Math.max(1, elapsed + 1), total: profile.courseLengthDays })}
          </div>
        </div>
        <div className="streak-box">
          <div className="streak-num">{formatNumber(progress.streak, lang)}</div>
          <div className="streak-lbl">{t("dashboard.dayStreak")}</div>
          <div style={{fontSize:"1.25rem",marginTop:4}}>🔥</div>
          {navigator.share && progress.streak > 0 && (
            <button
              onClick={handleShare}
              style={{
                marginTop:8, background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)",
                borderRadius:8, padding:"3px 10px", cursor:"pointer", color:"#fff",
                fontFamily:"'Nunito',sans-serif", fontSize:"0.6875rem", fontWeight:700,
                letterSpacing:"0.5px", display:"inline-flex", alignItems:"center", gap:4,
              }}>
              <span style={{fontSize:"0.75rem"}}>📤</span> {t("dashboard.share")}
            </button>
          )}
        </div>
      </div>

      {/* "My Teacher" badge — only for a referred student (profile.referredBy non-null).
          Renders nothing at all for an organic signup — same all-or-nothing visibility as the
          Teacher Dashboard link. Name is a denormalized copy (see linkStudentToTeacher). */}
      {profile.referredBy && (
        <div className="my-teacher-badge">
          <img className="my-teacher-badge-ic" src="/teacher-icon.png" alt="" aria-hidden="true" />
          <span dir="auto">{t("teacherDash.studentBadge", { name: profile.referredBy.teacherDisplayName })}</span>
        </div>
      )}

      {/* Teacher-partner only. Conditionally rendered (never in the DOM for a learner) — same
          role-gated pattern as the Header link and the Pro/trial badge; independent of
          subscription tier and trial state. */}
      {isTeacher(profile) && (
        <button className="teacher-dash-link" onClick={() => setScreen("teacherDashboard")}>
          <img className="teacher-dash-link-ic" src="/teacher-icon.png" alt="" aria-hidden="true" />
          <span>{t("teacherDash.link")}</span>
          <span className="teacher-dash-link-arrow" aria-hidden="true">→</span>
        </button>
      )}

      {/* Teacher Partner Terms — nag banner shown on every Home load until this teacher has an
          acceptance record for the CURRENT Terms version (checked against the backend, not a
          client flag). Hidden while the Terms are still in DRAFT (no acceptable version yet).
          Pulsates via CSS; falls back to a static gold ring under prefers-reduced-motion, and
          carries an icon + "Action needed" label so importance isn't conveyed by colour alone. */}
      {isTeacher(profile) && !TEACHER_TERMS_DRAFT && !teacherTermsAccepted && (
        <button
          className="tt-terms-banner"
          onClick={onGoToTeacherTerms}
          aria-label={`${t("teacherTerms.bannerTitle")}: ${t("teacherTerms.bannerBody")}`}
        >
          <span className="tt-terms-banner-ic" aria-hidden="true">⚠️</span>
          <span className="tt-terms-banner-body">
            <span className="tt-terms-banner-title">{t("teacherTerms.bannerTitle")}</span>
            <span className="tt-terms-banner-text">{t("teacherTerms.bannerBody")}</span>
          </span>
          <span className="tt-terms-banner-arrow" aria-hidden="true">→</span>
        </button>
      )}

      {/* Upgrade banner — shown to trial/expired users */}
      <UpgradeBanner profile={profile} subscription={subscription} premium={premium} onUpgrade={onUpgrade} />

      {/* Stats Row */}
      <div className="stats-row">
        {[
          { n: formatNumber(actual, lang, { grouping: true }), l: t("dashboard.wordsLearned") },
          { n: formatNumber(progress.lessonsCompleted, lang), l: t("dashboard.lessonsDone") },
          { n: formatNumber(pctGoal, lang) + "%", l: t("dashboard.goalComplete") },
          { n: formatNumber(Math.round(progress.totalMinutes / 60 * 10) / 10, lang) + " " + t("dashboard.hoursUnit"), l: t("dashboard.timeStudied") },
        ].map(s => (
          <div className="stat-card" key={s.l}>
            <div className="stat-num">{s.n}</div>
            <div className="stat-lbl">{s.l}</div>
          </div>
        ))}
      </div>

      {/* Pace Tracker */}
      <div className="pace-card">
        <div className="pace-header">
          <div className="pace-title">{t("dashboard.paceTitle")}</div>
          <div className={`pace-badge ${status}`}>{statusLabel[status]}</div>
        </div>
        <div style={{fontSize:"0.75rem",color:"#8B7355",marginBottom:8}}>
          {t("dashboard.paceTarget", { wpd: fmtWpd(profile.wordsPerDay), today: todayWords })}
        </div>
        <div className="pace-track">
          <div className="pace-bar-expected" style={{width: pctExpected + "%"}} />
          <div className="pace-bar-actual" style={{width: pctGoal + "%"}} />
        </div>
        <div className="pace-labels">
          <span>{formatNumber(0, lang)}</span>
          <span>{t("dashboard.paceExpected", { n: formatNumber(expected, lang, { grouping: true }) })}</span>
          <span>{t("dashboard.paceGoal", { n: formatNumber(profile.targetWords, lang, { grouping: true }) })}</span>
        </div>
      </div>

      {/* Action Cards */}
      <div className="actions-grid">
        <ActionCard icon="📖" color="#FFF3E0" onClick={() => setScreen("lesson")}
          title={t("dashboard.actionCurrentLesson")}
          sub={<><bdi>{selectedTopic ? getTranslatedTopicName(selectedTopic, lang) : ""}</bdi> · {formatNumber(selectedTopic?.words?.length || 0, lang)} {t("goals.words")}</>} />
        <ActionCard icon="📚" color="#F3E5F5" onClick={() => setScreen("allLessons")}
          title={t("dashboard.actionAllLessons")}
          sub={t("dashboard.lessonsSub", { count: getTopicCount(langCode), words: formatNumber(getWordCount(langCode), lang, { grouping: true }) })} />
        <ActionCard icon="📘" color="#E8EAF6" onClick={() => setScreen("grammarLab")}
          title={t("dashboard.actionGrammarLab")}
          sub={t("grammarLab.subtitle", { count: getGrammarPoints(langCode).length })} />
        <ActionCard icon="🎧" color="#FCE4EC" onClick={() => setScreen("listenSpeak")}
          title={t("dashboard.actionListenSpeak")}
          sub={t("dashboard.listenSpeakSub")} />
        <ActionCard icon="🃏" color="#E8F5E9" onClick={() => setScreen("flashcards")}
          title={t("dashboard.actionFlashcards")}
          sub={dueCards > 0 ? t("dashboard.cardsDue", { n: dueCards }) : t("dashboard.allCaughtUp")}
          badge={dueCards > 0 ? formatNumber(dueCards, lang) : null} />
        <ActionCard icon="📈" color="#E3F2FD" onClick={() => setScreen("progress")}
          title={t("dashboard.actionProgress")}
          sub={t("dashboard.progressSub")} />
        <ActionCard icon="📝" color="#FDF0E5" onClick={() => setScreen("myVocabulary")}
          title={t("dashboard.actionMyVocabulary")}
          sub={t("dashboard.myVocabSub")} />
      </div>

      {/* Tip of the day */}
      <div className="tip-card">
        <span style={{fontSize:"1.25rem"}}>💡</span>
        <div><strong>{t("dashboard.tipLabel")}</strong> {tip}</div>
      </div>
    </div>
  );
}

function ActionCard({ icon, color, title, sub, badge, onClick }) {
  return (
    <div className="action-card" onClick={onClick}>
      <div className="action-icon" style={{ background: color }}>{icon}</div>
      <div className="action-title">{title}</div>
      <div className="action-sub">{sub}</div>
      {badge && <div className="badge-count">{badge}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEACHER DASHBOARD
// ═══════════════════════════════════════════════════════════════
// Reached via the role-gated link in Header / Dashboard AND directly at /teacher-dashboard
// (real Hosting path — see the deep-link handling in App()). The Header/Home links are only
// *hidden* for a learner, so this component runs its OWN role check: a stale `screen` value
// (admin resets the role mid-session), a bookmark, or a hand-typed URL all land here, and a
// non-teacher is bounced straight back to the dashboard. Deliberately has NO subscription/trial
// gating — role and paid tier are independent.
//
// The referred-students list is fetched from the getMyReferredStudents Cloud Function, never by
// reading other users' docs client-side. Commission is a "coming soon" placeholder — the ledger
// is a later task and a bare $0 would read as "you've earned nothing" rather than "not tracked
// yet". The curriculum reference lives on the separate landing site, so it's an external link.
const TEACHER_CURRICULUM_URL = "https://speafy.com/curriculum";

// Lenient URL handling for the teacher's personal link. Real-world input rarely includes a
// scheme ("www.speafy.com", "speafy.com/me"), so a strict "must start with https://" check
// rejected valid links. Instead: blank is fine, otherwise prepend https:// when there's no
// scheme and accept anything that then parses to a host with a dot.
function normalizeUrl(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(s) ? s : `https://${s}`;
}
function looksLikeUrl(v) {
  const s = normalizeUrl(v);
  if (!s) return true;   // blank is allowed
  try {
    const u = new URL(s);
    return !!u.hostname && u.hostname.includes(".") && !/\s/.test(s);
  } catch {
    return false;
  }
}

function TeacherDashboard({ profile, user, onBack, onUpdateProfile, teacherTermsAccepted, onGoToTerms }) {
  const { t, lang } = useT();
  const allowed = isTeacher(profile);
  useEffect(() => { if (!allowed) onBack(); }, [allowed, onBack]);

  const referralLink = buildReferralLink(user?.uid);
  const [copied, setCopied] = useState(false);

  const [studentsState, setStudentsState] = useState({ status: "loading", students: [], capped: false });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    setStudentsState({ status: "loading", students: [], capped: false });
    getMyReferredStudents()
      .then((res) => {
        if (cancelled) return;
        setStudentsState({ status: "ready", students: res?.students || [], capped: !!res?.capped });
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("getMyReferredStudents failed:", e);
        setStudentsState({ status: "error", students: [], capped: false });
      });
    return () => { cancelled = true; };
  }, [allowed, reloadKey]);

  // Affiliate roadmap step 23 — payout-request status. Separate load from the students list
  // above: different callable, different loading lifecycle, and re-fetched on its own
  // payoutReloadKey after a successful request (so "eligible" flips to "already requested"
  // without needing to also re-run the unrelated students query).
  const [payoutState, setPayoutState] = useState({ status: "loading", balanceCents: 0, thresholdCents: 5000, eligible: false, hasOpenRequest: false });
  const [payoutReloadKey, setPayoutReloadKey] = useState(0);
  const [payoutConfirmOpen, setPayoutConfirmOpen] = useState(false);
  const [payoutRequestBusy, setPayoutRequestBusy] = useState(false);
  const [payoutRequestError, setPayoutRequestError] = useState(null);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    setPayoutState((s) => ({ ...s, status: "loading" }));
    getMyPayoutStatus()
      .then((res) => {
        if (cancelled) return;
        setPayoutState({
          status: "ready",
          balanceCents: res?.balanceCents || 0,
          thresholdCents: res?.thresholdCents || 5000,
          eligible: !!res?.eligible,
          hasOpenRequest: !!res?.hasOpenRequest,
        });
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("getMyPayoutStatus failed:", e);
        setPayoutState((s) => ({ ...s, status: "error" }));
      });
    return () => { cancelled = true; };
  }, [allowed, payoutReloadKey]);

  const confirmRequestPayout = async () => {
    setPayoutRequestBusy(true);
    setPayoutRequestError(null);
    try {
      await requestPayout();
      setPayoutConfirmOpen(false);
      setPayoutReloadKey((k) => k + 1);   // re-fetch — flips eligible/hasOpenRequest for real
    } catch (err) {
      console.error("requestPayout failed:", err);
      setPayoutRequestError(err?.message || true);
    } finally {
      setPayoutRequestBusy(false);
    }
  };

  const copyLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
    } catch {
      // Older/insecure-context fallback — select the field and use execCommand.
      const el = document.getElementById("td-referral-input");
      if (el) { el.focus(); el.select(); try { document.execCommand("copy"); } catch { /* give up quietly */ } }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Teacher profile edit form (profile.teacherProfile — client-writable, this is the
  //    teacher's own content, not an admin-controlled field like role/referredBy). Seeded once;
  //    this form is the only editor, matching how GoalsView/SettingsView seed from profile. ──
  const tp = profile?.teacherProfile || {};
  const [bio, setBio] = useState(tp.bio || "");
  const [photoUrl, setPhotoUrl] = useState(tp.photoUrl || "");   // a Storage download URL, set by upload
  const [personalLink, setPersonalLink] = useState(tp.personalLink || "");
  // Languages this teacher teaches (profile.teacherProfile.languagesTaught — an array of
  // TEACHABLE_LANGUAGES labels). Legacy accounts stored a free-text `subjectTaught` string
  // instead; seed from that with a best-effort name match so nobody loses their entry.
  const [languagesTaught, setLanguagesTaught] = useState(() => {
    if (Array.isArray(tp.languagesTaught)) {
      return tp.languagesTaught.filter((l) => TEACHABLE_LANGUAGES.includes(l));
    }
    const legacy = String(tp.subjectTaught || "").toLowerCase();
    return legacy ? TEACHABLE_LANGUAGES.filter((l) => legacy.includes(l.toLowerCase())) : [];
  });
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | urlError
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoErr, setPhotoErr] = useState(null);

  const editField = (setter) => (e) => { setter(e.target.value); setSaveState("idle"); };

  // Client-side gate (PNG/JPG, ≤200 KB) purely for a fast, clear message — storage.rules
  // enforces the same limits, so a bypassed check here still can't land a bad file.
  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";   // let the same file be re-picked after an error
    if (!file) return;
    setPhotoErr(null);
    if (!TEACHER_PHOTO_TYPES.includes(file.type)) { setPhotoErr(t("teacherDash.photoTypeErr")); return; }
    if (file.size > TEACHER_PHOTO_MAX_BYTES) { setPhotoErr(t("teacherDash.photoSizeErr")); return; }
    setPhotoBusy(true);
    try {
      const url = await uploadTeacherPhoto(user?.uid, file);
      setPhotoUrl(url);
      setSaveState("idle");
    } catch (err) {
      console.error("teacher photo upload failed:", err);
      setPhotoErr(t("teacherDash.photoUploadErr"));
    } finally {
      setPhotoBusy(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!looksLikeUrl(personalLink)) { setSaveState("urlError"); return; }
    const normalizedLink = normalizeUrl(personalLink);
    setSaveState("saving");
    // Keep only real, currently-selectable languages, in TEACHABLE_LANGUAGES order, deduped.
    const cleanLanguages = TEACHABLE_LANGUAGES.filter(
      (l) => SELECTABLE_TEACHABLE_LANGUAGES.includes(l) && languagesTaught.includes(l)
    );
    await onUpdateProfile({
      teacherProfile: {
        bio: bio.trim(),
        photoUrl,                       // already a clean Storage URL (or "")
        personalLink: normalizedLink,
        languagesTaught: cleanLanguages,
      },
    });
    setPersonalLink(normalizedLink);     // reflect the https:// we may have prepended
    setSaveState("saved");
  };

  if (!allowed) return null;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };
  const statusLabel = (s) =>
    s === "active" ? t("teacherDash.statusActive")
      : s === "trialing" ? t("teacherDash.statusTrial")
        : t("teacherDash.statusNone");

  // Step 3.3 — grammarTally is { [languageCode]: { [grammar_point_id]: count } } for an active
  // student, or null for a gated (non-active) one — see getMyReferredStudents (functions/
  // teacherActions.js). Client-side title resolution (per the investigation's recommendation):
  // getGrammarPoint/GRAMMAR_POINTS are already bundled for the existing Grammar Lab feature, so
  // this needs no server round trip and no duplicating grammar content into the Functions
  // runtime. null and an empty {} both render as the same "—" fmtDate already uses for "nothing
  // to show" elsewhere in this table — the disclaimer note below the table explains the gating
  // once, rather than repeating a reason in every empty cell. The per-language label (langCodes
  // below) only ever renders once a second target language actually exists — today's single-
  // language accounts never see it, so this is inert now but ready without a later rewrite.
  const renderGrammarFocus = (grammarTally) => {
    const langCodes = grammarTally ? Object.keys(grammarTally) : [];
    if (!langCodes.length) return <span className="td-grammar-empty">—</span>;
    return (
      <div className="td-grammar-chips">
        {langCodes.map((code) => (
          <div className="td-grammar-lang-group" key={code}>
            {langCodes.length > 1 && <span className="td-grammar-lang">{code}</span>}
            {Object.entries(grammarTally[code])
              .sort((a, b) => b[1] - a[1])
              .map(([gpId, count]) => (
                <span className="td-grammar-chip" key={`${code}-${gpId}`}>
                  {getGrammarPoint(code, gpId)?.title || gpId} · {formatNumber(count, lang)}
                </span>
              ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="teacher-dash td">
      <div className="lesson-header">
        <button className="back-btn" onClick={onBack}>{t("lesson.backBtn")}</button>
        <div className="lesson-title">{t("teacherDash.title")}</div>
      </div>
      <p className="td-intro">{t("teacherDash.intro")}</p>

      {/* Partner Terms status. Once accepted this is a read-only line + a plain link that opens
          the Terms (no second acceptance step). Before acceptance it links to the acceptance
          screen. Hidden entirely while the Terms are still in DRAFT. */}
      {!TEACHER_TERMS_DRAFT && (
        <p className="td-note" style={{ marginTop: 0, marginBottom: 14 }}>
          {teacherTermsAccepted ? (
            <>
              {t("teacherDash.termsStatusAccepted", { date: fmtDate(teacherTermsAccepted.acceptedAt) })}
              {" · "}
              {t("teacherDash.termsVersionLabel", { version: teacherTermsAccepted.termsVersion || TEACHER_TERMS_VERSION })}
              {" · "}
              <button type="button" className="td-linkbtn" onClick={onGoToTerms}>{t("teacherDash.termsView")}</button>
            </>
          ) : (
            <>
              {t("teacherDash.termsStatusPending")}
              {" · "}
              <button type="button" className="td-linkbtn" onClick={onGoToTerms}>{t("teacherTerms.bannerCta")}</button>
            </>
          )}
        </p>
      )}

      {/* 1 — Referral link */}
      <section className="td-section">
        <h2 className="td-h2"><span className="td-h2-icon" aria-hidden="true">🔗</span>{t("teacherDash.referralTitle")}</h2>
        <p className="td-p">{t("teacherDash.referralBody")}</p>
        <div className="td-link-row">
          <input
            id="td-referral-input"
            className="text-input td-link-input"
            type="text"
            readOnly
            value={referralLink}
            onFocus={(e) => e.target.select()}
            dir="ltr"
          />
          <button type="button" className="primary-btn td-copy-btn" onClick={copyLink} disabled={!referralLink}>
            {copied ? t("teacherDash.copied") : t("teacherDash.copy")}
          </button>
        </div>
        {referralLink && (
          <div className="td-share-row">
            <a className="td-share-btn td-share-btn--whatsapp" href={`https://wa.me/?text=${encodeURIComponent(t("teacherDash.shareText", { link: referralLink }))}`}
              target="_blank" rel="noopener noreferrer">
              <span aria-hidden="true">💬</span> {t("teacherDash.shareWhatsapp")}
            </a>
            <a className="td-share-btn td-share-btn--telegram" href={`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(t("teacherDash.shareTelegramText"))}`}
              target="_blank" rel="noopener noreferrer">
              <span aria-hidden="true">✈️</span> {t("teacherDash.shareTelegram")}
            </a>
            <a className="td-share-btn td-share-btn--email" href={`mailto:?subject=${encodeURIComponent(t("teacherDash.shareEmailSubject"))}&body=${encodeURIComponent(t("teacherDash.shareText", { link: referralLink }))}`}>
              <span aria-hidden="true">✉️</span> {t("teacherDash.shareEmail")}
            </a>
            <a className="td-share-btn td-share-btn--sms" href={`sms:?body=${encodeURIComponent(t("teacherDash.shareText", { link: referralLink }))}`}>
              <span aria-hidden="true">📱</span> {t("teacherDash.shareSms")}
            </a>
            {typeof navigator !== "undefined" && navigator.share && (
              <button type="button" className="td-share-btn td-share-btn--more" onClick={() => {
                navigator.share({ title: "Speafy", text: t("teacherDash.shareText", { link: referralLink }), url: referralLink }).catch(() => {});
              }}>
                <span aria-hidden="true">📤</span> {t("teacherDash.shareMore")}
              </button>
            )}
          </div>
        )}
      </section>

      {/* 2 + 3 — Referred students, with a "coming soon" commission column */}
      <section className="td-section">
        <h2 className="td-h2"><span className="td-h2-icon" aria-hidden="true">👥</span>{t("teacherDash.studentsTitle")}</h2>
        {studentsState.status === "loading" && <p className="td-p">{t("teacherDash.studentsLoading")}</p>}
        {studentsState.status === "error" && (
          <p className="td-p td-error">
            {t("teacherDash.studentsError")}{" "}
            <button type="button" className="td-linkbtn" onClick={() => setReloadKey((k) => k + 1)}>
              {t("teacherDash.retry")}
            </button>
          </p>
        )}
        {studentsState.status === "ready" && studentsState.students.length === 0 && (
          <p className="td-p td-empty">{t("teacherDash.studentsEmpty")}</p>
        )}
        {studentsState.status === "ready" && studentsState.students.length > 0 && (
          <>
            <div className="td-table-wrap">
              <table className="td-table">
                <thead>
                  <tr>
                    <th>{t("teacherDash.colName")}</th>
                    <th>{t("teacherDash.colJoined")}</th>
                    <th>{t("teacherDash.colStatus")}</th>
                    <th>{t("teacherDash.colCommission")}</th>
                    <th>{t("teacherDash.colGrammar")}</th>
                  </tr>
                </thead>
                <tbody>
                  {studentsState.students.map((s, i) => (
                    <tr key={i}>
                      <td>{s.firstName || t("teacherDash.studentUnnamed")}</td>
                      <td>{fmtDate(s.joinedAt)}</td>
                      <td>
                        <span className={`td-status td-status--${s.subscriptionStatus}`}>{statusLabel(s.subscriptionStatus)}</span>
                      </td>
                      <td>
                        <span className="td-commission-amount">{fmtMoneyCents(s.commissionCents, lang)}</span>
                        {s.capReached && (
                          <span className="td-commission-capnote">{t("teacherDash.commissionCapReached")}</span>
                        )}
                      </td>
                      <td>{renderGrammarFocus(s.grammarTally)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="td-note">{t("teacherDash.commissionNote")}</p>
            <p className="td-note">{t("teacherDash.grammarNote")}</p>
          </>
        )}
      </section>

      {/* Payout request (affiliate roadmap step 23). Separate load/section from the students
          table above — a different callable, and it needs its own confirm-before-submit flow. */}
      <section className="td-section">
        <h2 className="td-h2"><span className="td-h2-icon" aria-hidden="true">💰</span>{t("teacherDash.payoutTitle")}</h2>
        {payoutState.status === "loading" && <p className="td-p">{t("teacherDash.payoutLoading")}</p>}
        {payoutState.status === "error" && (
          <p className="td-p td-error">
            {t("teacherDash.payoutError")}{" "}
            <button type="button" className="td-linkbtn" onClick={() => setPayoutReloadKey((k) => k + 1)}>
              {t("teacherDash.retry")}
            </button>
          </p>
        )}
        {payoutState.status === "ready" && (
          <>
            <p className="td-p">
              {t("teacherDash.payoutBalance", { balance: fmtMoneyCents(payoutState.balanceCents, lang) })}
            </p>
            {payoutState.hasOpenRequest ? (
              <p className="td-note td-payout-pending">{t("teacherDash.payoutAlreadyRequested")}</p>
            ) : payoutState.eligible ? (
              <>
                <button
                  type="button"
                  className="primary-btn td-payout-btn"
                  onClick={() => { setPayoutRequestError(null); setPayoutConfirmOpen(true); }}
                >
                  {t("teacherDash.requestPayoutBtn")}
                </button>
                {payoutRequestError && <p className="td-p td-error">{t("teacherDash.payoutRequestFailed")}</p>}
              </>
            ) : (
              <p className="td-note">
                {t("teacherDash.payoutNotEligible", { threshold: fmtMoneyCents(payoutState.thresholdCents, lang) })}
              </p>
            )}
          </>
        )}
      </section>

      <ConfirmDialog
        open={payoutConfirmOpen}
        title={t("teacherDash.payoutConfirmTitle")}
        message={t("teacherDash.payoutConfirmBody", { balance: fmtMoneyCents(payoutState.balanceCents, lang) })}
        confirmLabel={t("teacherDash.payoutConfirmBtn")}
        cancelLabel={t("teacherDash.payoutConfirmCancel")}
        onConfirm={confirmRequestPayout}
        onCancel={() => { if (!payoutRequestBusy) setPayoutConfirmOpen(false); }}
        busy={payoutRequestBusy}
      />

      {/* 4 — Curriculum reference (external, landing site) */}
      <section className="td-section">
        <h2 className="td-h2"><span className="td-h2-icon" aria-hidden="true">📚</span>{t("teacherDash.curriculumTitle")}</h2>
        <p className="td-p">{t("teacherDash.curriculumBody")}</p>
        <a className="td-outlink" href={`${TEACHER_CURRICULUM_URL}?from=teacher-dashboard`} target="_blank" rel="noopener noreferrer">
          {t("teacherDash.curriculumLink")} <span aria-hidden="true">↗</span>
        </a>
      </section>

      {/* 5 — Editable teacher profile (future teacher directory reads profile.teacherProfile) */}
      <section className="td-section">
        <h2 className="td-h2"><span className="td-h2-icon" aria-hidden="true">🧑‍🏫</span>{t("teacherDash.profileTitle")}</h2>
        <p className="td-p">{t("teacherDash.profileBody")}</p>
        <form onSubmit={saveProfile} className="td-form">
          <label className="td-label">
            {t("teacherDash.profileBio")}
            <textarea className="text-input td-textarea" rows={3} value={bio} onChange={editField(setBio)}
              placeholder={t("teacherDash.profileBioPh")} maxLength={600} />
          </label>
          <div className="td-label">
            {t("teacherDash.profileLanguages")}
            <div className="td-lang-grid" role="group" aria-label={t("teacherDash.profileLanguages")}>
              {TEACHABLE_LANGUAGES.map((lang) => {
                const selectable = SELECTABLE_TEACHABLE_LANGUAGES.includes(lang);
                const on = languagesTaught.includes(lang);
                return (
                  <button
                    key={lang}
                    type="button"
                    className={`td-lang-btn${on ? " is-on" : ""}`}
                    aria-pressed={on}
                    disabled={!selectable}
                    title={selectable ? undefined : t("teacherDash.profileLanguagesSoon")}
                    onClick={() => {
                      setLanguagesTaught((prev) =>
                        prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]
                      );
                      setSaveState("idle");
                    }}
                  >
                    {lang}
                    {!selectable && <span className="td-lang-soon">{t("teacherDash.profileLanguagesSoon")}</span>}
                  </button>
                );
              })}
            </div>
            <span className="td-note">{t("teacherDash.profileLanguagesHint")}</span>
          </div>
          <div className="td-label">
            {t("teacherDash.profilePhoto")}
            <div className="td-photo-row">
              {photoUrl
                ? <img className="td-photo-preview" src={photoUrl} alt="" />
                : <div className="td-photo-preview td-photo-preview--empty" aria-hidden="true" />}
              <div className="td-photo-controls">
                <label className={`settings-action-btn td-photo-btn${photoBusy ? " is-busy" : ""}`}>
                  {photoBusy ? t("teacherDash.photoUploading") : t("teacherDash.photoChoose")}
                  <input type="file" accept="image/png,image/jpeg" onChange={onPickPhoto} disabled={photoBusy} />
                </label>
                {photoUrl && !photoBusy && (
                  <button type="button" className="td-linkbtn" onClick={() => { setPhotoUrl(""); setPhotoErr(null); setSaveState("idle"); }}>
                    {t("teacherDash.photoRemove")}
                  </button>
                )}
              </div>
            </div>
            <span className="td-note">{t("teacherDash.photoHint")}</span>
            {photoErr && <span className="td-error td-note">{photoErr}</span>}
          </div>
          <label className="td-label">
            {t("teacherDash.profileLink")}
            <input className="text-input" type="text" inputMode="url" value={personalLink} onChange={editField(setPersonalLink)}
              placeholder={t("teacherDash.profileLinkPh")} dir="ltr" />
          </label>
          {saveState === "urlError" && <p className="td-error td-note">{t("teacherDash.urlError")}</p>}
          <div className="td-form-actions">
            <button type="submit" className="primary-btn td-save-btn" disabled={saveState === "saving"}>
              {saveState === "saving" ? t("teacherDash.saving") : saveState === "saved" ? t("teacherDash.saved") : t("teacherDash.save")}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ALL LESSONS VIEW
// ═══════════════════════════════════════════════════════════════
function AllLessonsView({ setScreen, setSelectedTopic, progress, cards, completedTopics, onLimitReached, premium, frozenTopicIds, onUpgrade, profile }) {
  const { t, lang } = useT();
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();
  const curriculum = getCurriculum(langCode);
  const displayPos = new Map(curriculum.map((t, i) => [t.topic_id, i + 1]));
  const frozen = !premium;
  const [search, setSearch] = useState("");
  const filtered = search.trim()
    ? curriculum.filter(topic =>
        topic.topic.toLowerCase().includes(search.toLowerCase()) ||
        topic.words.some(w =>
          w.turkish.toLowerCase().includes(search.toLowerCase()) ||
          w.english.toLowerCase().includes(search.toLowerCase())
        )
      )
    : curriculum;

  const _today = todayISO();
  const totalDue = (cards || []).filter(c =>
    (!c.d1Mastered && c.due <= _today) || (c.d2 && !c.d2Mastered && c.d2.due <= _today)
  ).length;
  const overwhelmed = totalDue > 60;

  const todayLessons = (() => {
    const log = progress?.dailyLog?.find(l => l.date === _today);
    return log?.lessons || 0;
  })();

  const goToLesson = (topic) => {
    if (frozen && !frozenTopicIds.has(topic.topic_id)) { onUpgrade?.(); return; }
    if (overwhelmed && !completedTopics[topic.topic_id]) return;
    if (todayLessons >= DAILY_LESSON_LIMIT) { onLimitReached?.(); return; }
    setSelectedTopic(topic);
    setScreen("lesson");
  };

  // Jump straight to wherever the learner left off, instead of landing on topic #1 with
  // dozens of already-completed topics to scroll past. Runs once, before paint, so there's no
  // flash of the top-of-list before jumping (a useEffect here would paint at the old scroll
  // position first, then visibly animate away from it). Deliberately not "smooth" for the same
  // reason — this is a page-entry position restore, not a click-triggered motion like
  // LessonView's "Next Word" scroll, so it should read as "the list already starts here," not
  // as an animation. Only depends on mount: if completedTopics changes it's because the learner
  // finished a lesson and navigated back, which unmounts/remounts this view fresh anyway.
  const rowRefs = useRef(new Map());
  useLayoutEffect(() => {
    const nextTopic = curriculum.find(topic => !completedTopics[topic.topic_id]);
    const node = nextTopic && rowRefs.current.get(nextTopic.topic_id);
    if (node) node.scrollIntoView({ block: "center" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="all-lessons-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={() => setScreen("dashboard")}>{t("lesson.backBtn")}</button>
        <div>
          <div className="lesson-title">{t("allLessons.title")}</div>
          <div style={{fontSize:"0.8125rem",color:"#8B7355"}}>{t("allLessons.subtitle", { count: getTopicCount(langCode), words: formatNumber(getWordCount(langCode), lang, { grouping: true }) })}</div>
        </div>
      </div>

      {overwhelmed && (
        <div className="al-overwhelmed-overlay">
          <div className="al-overwhelmed-card">
            <div className="al-overwhelmed-icon">🃏</div>
            <h3 className="al-overwhelmed-title">{t("allLessons.overwhelmedLock")}</h3>
            <p className="al-overwhelmed-body">
              {t("allLessons.overwhelmedBanner", { n: totalDue })}
            </p>
            <button className="al-overwhelmed-btn" onClick={() => setScreen("flashcards")}>
              {t("allLessons.overwhelmedBtn")}
            </button>
          </div>
        </div>
      )}

      <div className="al-search-wrap">
        <input
          className="text-input al-search"
          placeholder={t("allLessons.searchPlaceholder")}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button className="al-clear-btn" onClick={() => setSearch("")}>✕</button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">{isolateInterpolation(t("allLessons.noResults"), "search", search)}</div>
      ) : (
        <div className="al-list">
          {filtered.map(topic => {
            const done = !!completedTopics[topic.topic_id];
            const locked = overwhelmed && !done;
            const paywalled = frozen && !frozenTopicIds.has(topic.topic_id);
            return (
              <button
                key={topic.topic_id}
                ref={node => {
                  if (node) rowRefs.current.set(topic.topic_id, node);
                  else rowRefs.current.delete(topic.topic_id);
                }}
                className={`al-topic-row${locked ? " al-topic-row--locked" : ""}${paywalled ? " al-topic-row--paywalled" : ""}`}
                onClick={() => goToLesson(topic)}
                disabled={locked}
              >
                <span className="al-order">#{formatNumber(displayPos.get(topic.topic_id), lang)}</span>
                <span className="al-name">
                  <bdi>{getTranslatedTopicName(topic, lang)}</bdi>
                  {paywalled && <span className="al-pro-tag">Pro</span>}
                </span>
                <span className="al-count" style={done ? { background: '#d1fae5', color: '#065f46' } : {}}>{formatNumber(topic.words.length, lang)} {t("goals.words")}</span>
                {locked
                  ? <span className="al-lock">🔒</span>
                  : paywalled
                    ? <span className="al-lock">🔒</span>
                    : <span className="al-arrow">→</span>
                }
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MY VOCABULARY VIEW
// ═══════════════════════════════════════════════════════════════
// BCP-47 tags for locale-aware date formatting, keyed by the app's base-language names.
const MV_DATE_LOCALES = {
  English: "en-US", Spanish: "es-ES", German: "de-DE", French: "fr-FR", Turkish: "tr-TR",
  Italian: "it-IT", Portuguese: "pt-PT", Russian: "ru-RU", Farsi: "fa-IR", Arabic: "ar-SA",
  Chinese: "zh-CN", Hindi: "hi-IN",
};
// Same 4-state memory model ProgressView shows (cardMemState, fsrs.js) — keeps the two views
// consistent instead of this one inventing its own new/learning/mastered buckets.
const MV_STATES = ["inAcquisition", "rooted", "fluent", "deeplyRetained"];
const MV_STATE_COLORS = { inAcquisition: "#64748b", rooted: "#10b981", fluent: "#059669", deeplyRetained: "#C9952A" };

function MyVocabularyView({ cards, completedTopics, profile, onBack }) {
  const { t, lang } = useT();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const baseLanguage = profile?.baseLanguage || "English";
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();

  const cardsById = new Map(cards.map(c => [c.id, c]));

  // Only words the learner has actually been taught (a flashcard exists for them), grouped by
  // topic in curriculum order — matches AllLessonsView's ordering.
  const encounteredTopics = getCurriculum(langCode).filter(topic => topic.words.some(w => cardsById.has(w.word_id)));
  const encounteredTopicIds = encounteredTopics.map(topic => topic.topic_id).join(",");

  // curriculum.js's word.english field is really "the meaning, in whatever base language" —
  // LessonView/FlashcardView overwrite it with the base-language translation pulled from the
  // per-topic /translations/ cache before display, but this view never did, so every learner
  // saw raw English regardless of base language. Unlike those two (which only ever resolve the
  // handful of topics in the current lesson/deck), My Vocabulary shows every topic the learner
  // has ever been taught — a live Claude call per topic here could mean upwards of a hundred
  // generations on one page load — so this reads the cache ONLY. A miss (base language switched
  // since a topic was learned, and never revisited via Lesson/Flashcards since) just falls back
  // to the English text, same as this view's behavior before the fix.
  const [translations, setTranslations] = useState({});
  useEffect(() => {
    if (baseLanguage === "English") { setTranslations({}); return; }
    let cancelled = false;
    Promise.all(encounteredTopics.map(topic =>
      getTranslationCache(langCode, topic.topic_id, baseLanguage, topic.words)
        .catch(() => null)
        .then(cached => {
          if (!cached) return null;
          const byWordId = new Map();
          topic.words.forEach((w, i) => { if (cached[i]?.meaning) byWordId.set(w.word_id, cached[i].meaning); });
          return [topic.topic_id, byWordId];
        })
    )).then(entries => {
      if (!cancelled) setTranslations(Object.fromEntries(entries.filter(Boolean)));
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounteredTopicIds, baseLanguage]);

  const topics = encounteredTopics.map(topic => {
    const byWordId = translations[topic.topic_id];
    const words = topic.words
      .map(w => ({ w, card: cardsById.get(w.word_id) }))
      .filter(x => x.card)
      .map(({ w, card }) => ({ word_id: w.word_id, turkish: w.turkish, english: byWordId?.get(w.word_id) || w.english, status: cardMemState(card).key }));
    return { topic, words };
  }).filter(x => x.words.length > 0);

  // A word_id can appear in several topics (later topics reuse earlier vocabulary for spaced
  // reinforcement) — that's correct for the per-topic lists below, but the summary counts must
  // dedupe by word_id or a word taught in 2 topics gets counted twice.
  const uniqueWords = [...new Map(topics.flatMap(x => x.words).map(w => [w.word_id, w])).values()];
  const statusCounts = uniqueWords.reduce(
    (acc, w) => { acc[w.status] = (acc[w.status] || 0) + 1; return acc; },
    { inAcquisition: 0, rooted: 0, fluent: 0, deeplyRetained: 0 }
  );

  const q = search.trim().toLowerCase();
  // Turkish dotted İ/i lowercases incorrectly under the default (non-Turkish) locale
  // (İ → i̇ instead of i), so "iyi" would silently fail to match "İyi günler" — compare
  // the Turkish field with the Turkish locale specifically.
  const qTr = search.trim().toLocaleLowerCase("tr-TR");
  const filteredTopics = topics
    .map(({ topic, words }) => ({
      topic,
      words: words.filter(w => {
        if (statusFilter && w.status !== statusFilter) return false;
        if (q && !w.turkish.toLocaleLowerCase("tr-TR").includes(qTr) && !w.english.toLowerCase().includes(q)) return false;
        return true;
      }),
    }))
    .filter(({ words }) => words.length > 0);

  const completedList = Object.entries(completedTopics || {})
    .map(([topicId, info]) => {
      const topic = getCurriculum(langCode).find(ct => ct.topic_id === topicId);
      return topic ? { topic, completedAt: info?.completedAt } : null;
    })
    .filter(Boolean)
    .sort((a, b) => (a.topic.order || 0) - (b.topic.order || 0));

  const dateLocale = MV_DATE_LOCALES[lang] || "en-US";

  return (
    <div className="goals-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={onBack}>{t("lesson.backBtn")}</button>
        <div className="lesson-title">{t("myVocab.title")}</div>
      </div>

      <div className="goals-card">
        <div className="goals-section-title">{t("myVocab.summaryTitle", { n: uniqueWords.length })}</div>
        <div className="mem-states-grid">
          {MV_STATES.map(key => (
            <div key={key} className="mem-state-card" style={{ borderTop: `3px solid ${MV_STATE_COLORS[key]}` }}>
              <div className="mem-state-count" style={{ color: MV_STATE_COLORS[key] }}>{formatNumber(statusCounts[key], lang)}</div>
              <div className="mem-state-label">{t(`progress.${key}`)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="al-search-wrap">
        <input
          className="text-input al-search"
          placeholder={t("myVocab.searchPlaceholder")}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && <button className="al-clear-btn" onClick={() => setSearch("")}>✕</button>}
      </div>
      <div className="gl-filter-row">
        <select className="gl-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">{t("myVocab.allStatuses")}</option>
          {MV_STATES.map(key => <option key={key} value={key}>{t(`progress.${key}`)}</option>)}
        </select>
      </div>

      {filteredTopics.length === 0 ? (
        <div className="empty-state">
          {q ? isolateInterpolation(t("myVocab.noResults"), "search", search) : t("myVocab.noWords")}
        </div>
      ) : (
        filteredTopics.map(({ topic, words }) => (
          <div className="goals-card" key={topic.topic_id}>
            <div className="mv-topic-header">
              <div className="mv-topic-name"><bdi>{getTranslatedTopicName(topic, lang)}</bdi></div>
              <span className="al-count">{formatNumber(words.length, lang)} {t("goals.words")}</span>
            </div>
            <div className="mv-word-list">
              {words.map(w => (
                <div className="mv-word-row" key={w.word_id}>
                  <div className="mv-word-text">
                    <span className="mv-word-turkish"><bdi>{w.turkish}</bdi></span>
                    <span className="mv-word-meaning"><bdi>{w.english}</bdi></span>
                  </div>
                  <span className={`gl-badge mv-badge-${w.status.toLowerCase()}`}>{t(`progress.${w.status}`)}</span>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {completedList.length > 0 && (
        <div className="goals-card">
          <div className="goals-section-title">{t("myVocab.completedTopicsTitle", { n: completedList.length, total: getTopicCount(langCode) })}</div>
          <div className="mv-completed-list">
            {completedList.map(({ topic, completedAt }) => (
              <div className="mv-completed-row" key={topic.topic_id}>
                <span className="mv-completed-name"><bdi>{getTranslatedTopicName(topic, lang)}</bdi></span>
                <span className="al-count">{formatNumber(topic.words.length, lang)} {t("goals.words")}</span>
                <span className="mv-completed-date">{completedAt ? new Date(completedAt).toLocaleDateString(dateLocale) : t("myVocab.noDate")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// GOALS VIEW
// ═══════════════════════════════════════════════════════════════
function GoalsView({ profile, onSave, onSwitchTargetLanguage, onBack, onTakePlacementTest }) {
  const { t, lang: uiLang } = useT();
  const targetLang = profile?.targetLanguage || "Turkish";
  const availableLangs = BASE_LANGS.filter(l => l.toLowerCase() !== targetLang.toLowerCase());
  const initLang = (() => {
    const bl = profile?.baseLanguage || "English";
    return bl.toLowerCase() === targetLang.toLowerCase() ? "English" : bl;
  })();
  const [selectedTargetLang, setSelectedTargetLang] = useState(targetLang);

  const [displayName, setDisplayName] = useState(profile?.name || "");
  const [words, setWords] = useState(profile?.targetWords || 3000);
  const [durNum, setDurNum] = useState(Math.max(1, Math.min(48, Math.round((profile?.courseLengthDays || 180) / 30))));
  const [lang, setLang] = useState(initLang);
  const [showCustom, setShowCustom] = useState(!availableLangs.includes(initLang));
  const [customInput, setCustomInput] = useState(!availableLangs.includes(initLang) ? initLang : "");
  const [exitMsg, setExitMsg] = useState(false);
  const saveTimer = useRef(null);
  const mounted = useRef(false);

  // Always-current snapshot — avoids stale closures in persistRef
  const latestRef = useRef({ displayName, words, durNum, lang });
  useEffect(() => { latestRef.current = { displayName, words, durNum, lang }; });

  // persistRef is always the latest version of the save function
  const persistRef = useRef(null);
  persistRef.current = (overrideLang) => {
    const { displayName: dn, words: w, durNum: dur, lang: l } = latestRef.current;
    const finalLang = overrideLang || l;
    const d = dur * 30;
    onSave({ ...profile, name: dn.trim() || profile?.name, targetWords: w, courseLengthDays: d, wordsPerDay: d > 0 ? w / d : 0, baseLanguage: finalLang });
  };

  const days = Math.round(durNum * 30);
  const wpd = days > 0 ? words / days : 0;
  const pct = ((words - TURKISH_MIN) / (MAX_WORDS - TURKISH_MIN)) * 100;

  // Debounced auto-save on any change
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistRef.current(), 800);
    return () => clearTimeout(saveTimer.current);
  }, [displayName, words, durNum, lang]);

  // Save on unmount — catches all exit paths: nav links, browser close, tab switch
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      persistRef.current();
    };
  }, []);

  const handleBack = () => {
    setExitMsg(true);
    setTimeout(() => onBack(), 1000);
  };

  const selectLang = (l) => { setLang(l); setShowCustom(false); setCustomInput(""); };

  const confirmCustom = () => {
    const val = customInput.trim();
    if (!val || val.toLowerCase() === targetLang.toLowerCase()) return;
    setLang(val);
    setShowCustom(false);
    persistRef.current(val);
  };

  const handleTargetLangChange = (newLang) => {
    if (newLang === selectedTargetLang) return;
    setSelectedTargetLang(newLang);
    onSwitchTargetLanguage(newLang);
  };

  return (
    <div className="goals-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={handleBack}>{t("lesson.backBtn")}</button>
        <div className="lesson-title">{t("goals.title")}</div>
      </div>

      <div className="goals-card">
        <div className="goals-section-title">{t("goals.displayName")}</div>
        <input className="text-input" value={displayName} onChange={e => setDisplayName(e.target.value)}
          placeholder={t("goals.namePlaceholder")} style={{ marginBottom: 0 }} />
      </div>

      <div className="goals-card">
        <div className="goals-section-title">{t("goals.targetLanguage")}</div>
        <select
          value={selectedTargetLang}
          onChange={e => handleTargetLangChange(e.target.value)}
          className="text-input target-lang-sel"
          style={{ marginBottom: 0, cursor: TARGET_LANGUAGES.length > 1 ? "pointer" : "default" }}
        >
          {TARGET_LANGUAGES.map(l => (
            <option key={l.code} value={l.label}>
              {t(`goals.targetLangNames.${l.label}`) || l.label}
            </option>
          ))}
        </select>
      </div>

      <div className="goals-card">
        <div className="goals-section-title">{t("goals.vocabTarget")}</div>
        <div className="slider-val">{formatNumber(words, uiLang, { grouping: true })} <span>{t("goals.words")}</span></div>
        <input type="range" className="slider" dir="ltr" min={TURKISH_MIN} max={MAX_WORDS} step={100}
          value={words} onChange={e => setWords(+e.target.value)} style={{ "--pct": pct + "%" }} />
        <div className="slider-marks" dir="ltr"><span>{t("goals.minWords")}</span><span>{t("goals.midWords")}</span><span>{t("goals.maxWords")}</span></div>
      </div>

      <div className="goals-card">
        <div className="goals-section-title">{t("goals.courseDuration")}</div>
        <div className="dur-row">
          <input type="number" className="text-input dur-num" min={1} max={48}
            value={durNum} step={0.1} onChange={e => setDurNum(Math.max(1, Math.min(48, Math.round(+e.target.value * 10) / 10)))} />
          <div className="text-input dur-sel" style={{display:"flex",alignItems:"center",justifyContent:"center",background:"#F5EDE0",color:"#5C3D2E",fontWeight:700}}>{t("goals.months")}</div>
        </div>
        <p style={{ fontSize: 13, color: "#8B7355", marginTop: 4 }}>
          {t("goals.daysSummary", { days })}
        </p>
      </div>

      <div className="goals-card">
        <div className="goals-section-title">{t("goals.baseLanguage")}</div>
        <div className="lang-grid">
          {BASE_LANGS.map(l => {
            const isTarget = l.toLowerCase() === targetLang.toLowerCase();
            return (
              <button key={l} className={`lang-btn${lang===l?" sel":""}${isTarget?" tgt-disabled":""}`}
                onClick={() => { if (!isTarget) selectLang(l); }} disabled={isTarget}>
                {t(`goals.langNames.${l}`) || l}
              </button>
            );
          })}
        </div>
        {/* Disabled 2026-07-25: incomplete-UI languages (no static i18n bundle and/or no active
            lesson-content translation, per constants.js BASE_LANGS/EXTRA_LANGS) shipped a
            confusing partial experience. Re-enable (drop `disabled`, restore the onClick) once
            more languages are added — tracked in speafy_dev_task_backlog.md. */}
        <button
          className="lang-btn"
          style={{ width: "100%", marginTop: 8, textAlign: "center", opacity: 0.5, cursor: "not-allowed" }}
          disabled
        >
          {!availableLangs.includes(lang) ? `✓ ${lang}` : t("goals.otherLanguage")}
        </button>
        {showCustom && (
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "stretch" }}>
            <select
              value={customInput}
              onChange={e => setCustomInput(e.target.value)}
              style={{ flex: 1, minWidth: 0, padding: "9px 8px", border: "2px solid #E8D5B7", borderRadius: 10, fontFamily: "'Nunito',sans-serif", fontSize: 12, fontWeight: 600, color: "#5C3D2E", background: "#fff", cursor: "pointer", outline: "none" }}
            >
              <option value="">{t("goals.selectLanguage")}</option>
              {EXTRA_LANGS.filter(l => l.toLowerCase() !== targetLang.toLowerCase()).map(l => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
            <button onClick={confirmCustom} style={{ flexShrink: 0, padding: "9px 4px", width: "calc(25% - 6px)", border: "2px solid #7B1E1E", borderRadius: 10, background: "#7B1E1E", color: "#fff", fontFamily: "'Nunito',sans-serif", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t("goals.ok")}</button>
          </div>
        )}
      </div>

      <div className="summary-grid" style={{ margin: "16px 0" }}>
        <div className="sum-item"><div className="sum-num">{formatNumber(words, uiLang, { grouping: true })}</div><div className="sum-lbl">{t("goals.wordsLbl")}</div></div>
        <div className="sum-item accent"><div className="sum-num">{formatNumber(fmtWpd(wpd), uiLang)}</div><div className="sum-lbl">{t("goals.wordsPerDay")}</div></div>
        <div className="sum-item"><div className="sum-num">{formatNumber(days, uiLang)}</div><div className="sum-lbl">{t("goals.daysLbl")}</div></div>
      </div>

      {/* Relocated from Settings' "Learning" section — same button/behavior, just a new home. */}
      <div className="goals-card">
        <div className="goals-section-title">{t("settings.learningSection")}</div>
        <div className="settings-row" style={{borderBottom:"none",paddingBottom:0}}>
          <div>
            <div className="settings-row-label">{t("settings.placementTestLabel")}</div>
            <div className="settings-row-sub">
              {profile?.placementTestLevel
                ? t("settings.placementTestSubRetake", { level: profile.placementTestLevel })
                : t("settings.placementTestSubNew")}
            </div>
          </div>
          <button className="settings-action-btn" onClick={onTakePlacementTest}>
            {profile?.placementTestLevel ? t("settings.placementTestBtnRetake") : t("settings.placementTestBtnNew")}
          </button>
        </div>
      </div>

      {exitMsg && <div className="success-msg">{t("goals.saved")}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PROGRESS VIEW
// ═══════════════════════════════════════════════════════════════
function ProgressView({ progress, profile, cards, onBack }) {
  const { t, lang } = useT();
  if (!progress || !profile) return null;
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();
  const today = todayISO();
  const log = progress.dailyLog || [];

  // Average daily study time over the last 14 calendar days (today included), counting
  // days with no logged activity as 0 minutes — not just an average over active days.
  const avgMinutes14d = Math.round(
    Array.from({ length: 14 }, (_, i) => log.find(l => l.date === addDays(today, -i))?.minutes || 0)
      .reduce((sum, m) => sum + m, 0) / 14
  );

  // Memory state distribution
  const dist = cards.reduce((acc, c) => {
    const iv = c.interval || 0;
    if (!c.stability || iv < 3) acc[0]++;
    else if (iv < 21) acc[1]++;
    else if (iv < 90) acc[2]++;
    else acc[3]++;
    return acc;
  }, [0, 0, 0, 0]);
  const totalCards = cards.length;

  // 15-day review forecast (length 16 = Today + 15 future days)
  const forecast = Array.from({ length: 16 }, (_, i) => {
    const date = addDays(today, i);
    return {
      day: i === 0 ? t("progress.today") : i === 1 ? t("progress.tmrw") : t("progress.plusDays", { n: i }),
      dayFull: i === 0 ? t("progress.todayFull") : i === 1 ? t("progress.tmrwFull") : t("progress.plusDaysFull", { n: i }),
      count: cards.filter(c => i === 0 ? c.due <= date : c.due === date).length
    };
  });

  // Topic ring data: group cards by topic
  const topicMap = {};
  cards.forEach(c => {
    const key = c.topicId || c.topicName;
    if (!key) return;
    if (!topicMap[key]) topicMap[key] = { name: c.topicName, order: c.topicOrder, topicId: c.topicId, cards: [] };
    topicMap[key].cards.push(c);
  });
  const topicList = Object.values(topicMap)
    .map(tp => {
      const avgS = tp.cards.reduce((s, c) => s + (c.stability || 0), 0) / tp.cards.length;
      // Older cards may lack topicId (pre-dates its addition to fsrs.js) — those fall back
      // to the English name already cached on the card, same as before this feature.
      const curriculumTopic = tp.topicId && getCurriculum(langCode).find(t => t.topic_id === tp.topicId);
      const displayName = curriculumTopic ? getTranslatedTopicName(curriculumTopic, lang) : tp.name;
      // Resolve order live from CURRICULUM rather than the value baked onto the card at
      // creation time, so re-sequencing topics doesn't leave existing learners' rings sorted
      // by a stale order. Falls back to the card's baked-in order for the topicId-less cards above.
      const order = curriculumTopic ? curriculumTopic.order : tp.order;
      return { ...tp, order, avgS, displayName, repCard: { stability: avgS || null, lastReview: tp.cards[0]?.lastReview, interval: Math.round(avgS) } };
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Vocabulary growth chart
  const todayStr = todayISO();
  const rawChartData = log.length > 0
    ? log.map((l, i) => {
        const isToday = l.date === todayStr;
        return {
          day: t("progress.dayLabel", { n: i + 1 }),
          words: log.slice(0, i + 1).reduce((s, x) => s + (x.words || 0), 0),
          fluent:   isToday ? dist[2] : (l.fluent   ?? null),
          retained: isToday ? dist[3] : (l.retained ?? null),
        };
      })
    : [{ day: t("progress.dayLabel", { n: 1 }), words: 0, fluent: 0, retained: 0 }];
  // Fill historical nulls with proportional estimates so lines render immediately
  const totalNow = rawChartData[rawChartData.length - 1]?.words || 1;
  const chartData = rawChartData.map(d => ({
    ...d,
    fluent:   d.fluent   !== null ? d.fluent   : Math.round((d.words / totalNow) * dist[2]),
    retained: d.retained !== null ? d.retained : Math.round((d.words / totalNow) * dist[3]),
  }));
  const pct = Math.min(100, Math.round((progress.wordsLearned / profile.targetWords) * 100));

  const memStates = [
    { key: 'inAcquisition', count: dist[0], color: '#64748b', descKey: 'acqDesc' },
    { key: 'rooted',        count: dist[1], color: '#10b981', descKey: 'rootedDesc' },
    { key: 'fluent',        count: dist[2], color: '#059669', descKey: 'fluentDesc' },
    { key: 'deeplyRetained',count: dist[3], color: '#C9952A', descKey: 'retainedDesc' },
  ];

  return (
    <div className="goals-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={onBack}>{t("lesson.backBtn")}</button>
        <div className="lesson-title">{t("progress.title")}</div>
      </div>

      {/* Memory State Distribution */}
      {totalCards > 0 && (
        <div className="goals-card">
          <div className="goals-section-title">{t("progress.memDistTitle", { n: totalCards })}</div>
          <div className="mem-states-grid">
            {memStates.map(s => (
              <div key={s.key} className="mem-state-card" style={{ borderTop: `3px solid ${s.color}` }}>
                <div className="mem-state-count" style={{ color: s.color }}>{formatNumber(s.count, lang)}</div>
                <div className="mem-state-label">{t(`progress.${s.key}`)}</div>
                <div className="mem-state-desc">{t(`progress.${s.descKey}`)}</div>
                <div className="mem-state-bar">
                  <div style={{ width: `${totalCards ? (s.count / totalCards) * 100 : 0}%`, background: s.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Review Forecast */}
      {totalCards > 0 && (
        <div className="goals-card">
          <div className="goals-section-title">{t("progress.forecastTitle")}</div>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={forecast} barSize={14} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <XAxis dataKey="day" tick={{ fontSize: '0.625rem', fill: '#8B7355' }} />
              <YAxis tick={{ fontSize: '0.625rem', fill: '#8B7355' }} allowDecimals={false} tickFormatter={v => formatNumber(v, lang)} />
              <Tooltip contentStyle={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', borderColor: '#F0DFC0' }}
                labelFormatter={(_, payload) => payload && payload[0] ? payload[0].payload.dayFull : ''}
                formatter={v => [formatNumber(v, lang), t("progress.cardsDue")]} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {forecast.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? '#7B1E1E' : '#C9952A'} fillOpacity={i === 0 ? 1 : 0.65} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Goal Progress */}
      <div className="goals-card">
        <div className="goals-section-title">{t("progress.goalProgress")}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
          <div style={{ flex: 1, background: '#F5E9D8', borderRadius: 8, height: 14 }}>
            <div style={{ width: pct + '%', height: '100%', background: 'linear-gradient(90deg,#7B1E1E,#C9952A)', borderRadius: 8, transition: 'width 0.5s' }} />
          </div>
          <span style={{ fontWeight: 700, color: '#7B1E1E', fontSize: 15, minWidth: 40 }}>{formatNumber(pct, lang)}%</span>
        </div>
        <p style={{ fontSize: 13, color: '#8B7355' }}>{t("progress.wordsLearned", { learned: formatNumber(progress.wordsLearned, lang, { grouping: true }), target: formatNumber(profile.targetWords, lang, { grouping: true }) })}</p>
      </div>

      {/* Vocabulary Growth Chart */}
      <div className="goals-card">
        <div className="goals-section-title">{t("progress.vocabGrowth")}</div>
        {log.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#8B7355', fontSize: 14 }}>{t("progress.noChart")}</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="wg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7B1E1E" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#7B1E1E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0DFC0" />
              <XAxis dataKey="day" tick={{ fontSize: '0.6875rem', fill: '#8B7355' }} />
              <YAxis tick={{ fontSize: '0.6875rem', fill: '#8B7355' }} tickFormatter={v => formatNumber(v, lang)} />
              <Tooltip contentStyle={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8125rem', borderColor: '#F0DFC0' }}
                formatter={v => formatNumber(v, lang)} />
              <Legend wrapperStyle={{ fontSize: '0.75rem', fontFamily: 'Nunito, sans-serif', paddingTop: 8 }} />
              <Area type="monotone" dataKey="words" stroke="#7B1E1E" fill="url(#wg)" strokeWidth={3} name={t("progress.wordsLearnedSeries")} connectNulls={false} />
              <Line type="monotone" dataKey="fluent" stroke="#059669" strokeWidth={1.5} dot={false} name={t("memStates.fluent")} connectNulls={false} />
              <Line type="monotone" dataKey="retained" stroke="#C9952A" strokeWidth={1.5} dot={false} name={t("memStates.deeplyRetained")} connectNulls={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Per-Topic Memory Rings */}
      {topicList.length > 0 && (
        <div className="goals-card">
          <div className="goals-section-title">{t("progress.topicRings")}</div>
          <div className="topic-ring-list">
            {topicList.map(tp => {
              const { key: stateKey, color } = cardMemState(tp.repCard);
              return (
                <div key={tp.name} className="topic-ring-row">
                  <ProgressRing card={tp.repCard} size={32} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="topic-ring-name"><bdi>{tp.displayName}</bdi></div>
                    <div className="topic-ring-meta" style={{ color }}>{t(`memStates.${stateKey}`)}</div>
                  </div>
                  <div className="topic-ring-count">{formatNumber(tp.cards.length, lang)} {t("progress.cards")}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="goals-card">
        <div className="goals-section-title">{t("progress.quickStats")}</div>
        <div className="summary-grid" style={{gridTemplateColumns:"repeat(2,1fr)"}}>
          <div className="sum-item"><div className="sum-num">{formatNumber(progress.streak, lang)}</div><div className="sum-lbl">{t("progress.dayStreak")}</div></div>
          <div className="sum-item accent"><div className="sum-num">{formatNumber(progress.lessonsCompleted, lang)}</div><div className="sum-lbl">{t("progress.lessonsDone")}</div></div>
          <div className="sum-item"><div className="sum-num">{formatNumber(Math.round(progress.totalMinutes / 60 * 10) / 10, lang)} {t("dashboard.hoursUnit")}</div><div className="sum-lbl">{t("progress.totalStudy")}</div></div>
          <div className="sum-item"><div className="sum-num">{formatNumber(avgMinutes14d, lang)} {t("progress.minUnit")}</div><div className="sum-lbl">{t("progress.avg14Day")}</div></div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PAYWALL SCREEN
// ═══════════════════════════════════════════════════════════════
function PaywallScreen({ profile, subscription, onUpgrade, onGoToSettings, onBack }) {
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();
  const [billing, setBilling] = useState("yearly");
  // A6: this screen is only ever reachable pre-subscription (the "active"/"paused" branches
  // below return early otherwise) — a fresh checkout for either tier carries no
  // double-subscription risk, so a first-time subscriber can go straight to Elite here, not
  // just Pro. Defaults to "pro" (the previously-only option) so nothing changes for someone
  // who doesn't touch the new selector.
  const [tier, setTier] = useState("pro");
  const { t, lang } = useT();
  const features = t(tier === "elite" ? "paywall.eliteFeatures" : "paywall.features");

  // Duplicate-purchase guard: an already-active subscriber landing on this
  // screen (e.g. stale nav state, or a checkout.closed redirect that fires
  // before this component re-renders) must never see a re-purchasable CTA —
  // this is exactly the gap that let one real sandbox account complete two
  // separate checkouts (monthly then yearly, ~49s apart) for itself.
  if (subscription?.status === "active") {
    return (
      <div className="paywall-screen">
        <button className="back-btn" onClick={onBack}>{t("paywall.back")}</button>
        <div className="paywall-card">
          <div className="paywall-lock">✓</div>
          <h2 className="paywall-title">{t("paywall.alreadyProTitle")}</h2>
          <p className="paywall-sub">{t("paywall.alreadyProSub")}</p>
          <button className="paywall-cta" onClick={onBack}>
            {t("paywall.alreadyProGoHomeBtn")}
          </button>
        </div>
      </div>
    );
  }

  // A paused user already has a live Paddle subscription — routing them
  // through onUpgrade here would open a brand-new checkout (real risk of a
  // duplicate subscription), so show a reduced screen pointing at Settings
  // (where the actual Resume action lives) instead of pricing/checkout.
  if (subscription?.status === "paused") {
    return (
      <div className="paywall-screen">
        <button className="back-btn" onClick={onBack}>{t("paywall.back")}</button>
        <div className="paywall-card">
          <div className="paywall-lock">⏸</div>
          <h2 className="paywall-title">{t("paywall.pausedTitle")}</h2>
          <p className="paywall-sub">{t("paywall.pausedSub")}</p>
          <button className="paywall-cta" onClick={() => onGoToSettings?.()}>
            {t("paywall.pausedGoToSettingsBtn")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="paywall-screen">
      <button className="back-btn" onClick={onBack}>{t("paywall.back")}</button>
      <div className="paywall-card">
        <div className="paywall-lock">🔒</div>
        <h2 className="paywall-title">{t("paywall.title")}</h2>
        <p className="paywall-sub">
          {profile?.trialStartDate && trialDaysLeft(profile) === 0
            ? t("paywall.subTrialExpired")
            : t("paywall.subDefault")}
        </p>

        <div className="billing-toggle" style={{margin:"16px auto"}}>
          <button className={`billing-btn${tier === "pro" ? " active" : ""}`} onClick={() => setTier("pro")}>{t("paywall.tierPro")}</button>
          <button className={`billing-btn${tier === "elite" ? " active" : ""}`} onClick={() => setTier("elite")}>{t("paywall.tierElite")}</button>
        </div>

        <div className="paywall-features">
          {(Array.isArray(features) ? features : []).map((f, i) => (
            <div key={i} className="paywall-feature-row"><span className="paywall-check">✓</span>{t((tier === "elite" ? "paywall.eliteFeatures." : "paywall.features.") + i, { count: getTopicCount(langCode) }) || f}</div>
          ))}
        </div>

        <div className="billing-toggle" style={{margin:"20px auto 16px"}}>
          <button className={`billing-btn${billing === "monthly" ? " active" : ""}`} onClick={() => setBilling("monthly")}>{t("paywall.billingMonthly")}</button>
          <button className={`billing-btn${billing === "yearly" ? " active" : ""}`} onClick={() => setBilling("yearly")}>
            {t("paywall.billingYearly")} <span className="save-badge">{t("paywall.saveBadge")}</span>
          </button>
        </div>

        <div className="paywall-price">
          {tier === "elite"
            ? (billing === "monthly"
                ? <><strong>${formatNumber(ELITE_PRICE_MONTHLY_USD, lang)}</strong><span>{t("paywall.pricePerMonth")}</span></>
                : <><strong>${formatNumber(Math.round(ELITE_PRICE_YEARLY_USD / 12 * 100) / 100, lang)}</strong><span>{t("paywall.pricePerMonth")} · ${formatNumber(ELITE_PRICE_YEARLY_USD, lang)} {t("paywall.priceBilledYearly")}</span></>)
            : (billing === "monthly"
                ? <><strong>${formatNumber(PRICE_MONTHLY_USD, lang)}</strong><span>{t("paywall.pricePerMonth")}</span></>
                : <><strong>${formatNumber(Math.round(PRICE_ANNUAL_USD / 12 * 100) / 100, lang)}</strong><span>{t("paywall.pricePerMonth")} · ${formatNumber(PRICE_ANNUAL_USD, lang)} {t("paywall.priceBilledYearly")}</span></>)}
        </div>

        <button className="paywall-cta" onClick={() => onUpgrade?.(billing, tier)}>
          {t(tier === "elite" ? "paywall.ctaElite" : "paywall.cta")}
        </button>
        <p className="paywall-fine">{t("paywall.fine")}</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// UPGRADE BANNER
// ═══════════════════════════════════════════════════════════════
function UpgradeBanner({ profile, subscription, premium, onUpgrade }) {
  const { t } = useT();
  // Covers admin accounts and simple_viewer (QA/friends access) as well as an active/trialing
  // Paddle subscription or a grandfathered pre-trial profile — see isPremiumActive. Checking
  // subscription.status alone here (as this used to) missed those free-access bypass roles
  // entirely, so a simple_viewer within their original 7-day trial window still saw this banner.
  if (premium) return null;
  if (subscription?.status === "active") return null;
  // Paused status is communicated via the header badge and the Settings
  // subscription card — showing an "Upgrade to Pro" banner to someone who
  // already has a Pro subscription (just paused) would be misleading.
  if (subscription?.status === "paused") return null;
  if (!profile?.trialStartDate) return null; // grandfathered — no banner

  const daysLeft = trialDaysLeft(profile);
  const expired = daysLeft === 0;

  return (
    <div className={`upgrade-banner${expired ? " expired" : daysLeft <= 2 ? " warning" : ""}`}>
      <span className="upgrade-banner-text">
        {expired
          ? t("paywall.bannerExpired")
          : t("paywall.bannerActive", { n: daysLeft, s: daysLeft !== 1 ? "s" : "" })}
      </span>
      <button className="upgrade-banner-btn" onClick={() => onUpgrade?.()}>
        {t("paywall.bannerBtn")}
      </button>
    </div>
  );
}

